// UltimateDigitalThreadVisualization.js - COMPLETE 2700+ LINE VERSION
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { DIGITAL_THREADS } from '../data/digitalThreads.js'; // Import the digital threads data

import { QuantumLayoutSelector, useQuantumLayoutTransformer } from './QuantumLayoutTransformer';
import { createTickmarkSystem, calculateThreadStats } from './QuantumLayoutTransformer/utils';

import ThreadControlPanel from './QuantumLayoutTransformer/ThreadControlPanel';



// Define all functions, domains, and systems
const allFunctions = [
  'Marketing', 'Sales', 'Engineering', 'QA', 'Regulatory', 'R&D', 'Mfg Eng', 
  'Test', 'Certification', 'SCM', 'Service', 'Field Ops', 'Warranty', 'Ops', 
  'Finance', 'PMO', 'Compliance', 'HR', 'IT Sec', 'Legal', 'Data Science', 
  'Product Mgmt', 'UX', 'ESG', 'Exec Mgmt', 'Maintenance'
];

const allDomains = [
  'Sys Eng', 'Mechanical', 'Electrical', 'Software', 'MCAD', 'ECAD', 'MBSE',
  'Structural', 'Thermal', 'CFD', 'Controls', 'eBOM', 'mBOM', 'sBOM', 'Variants',
  'Process', 'Tooling', 'Plant Eng', 'Mechatronics', 'Telemetry', 'Firmware',
  'Eco materials', 'Recycling', 'Cost allocations', 'Templates', 'Best Practices',
  'Secure MCAD', 'Secure ECAD', 'Secure SW', 'Aero certs', 'Auto certs', 'Med certs',
  'Milestones', 'Deliverables', 'FEA', 'Multiphysics', 'Options', 'Ergonomics',
  'AR/VR', 'HMI', 'Asset health', 'AI MCAD', 'AI ECAD', 'AI SW', 'UX design',
  'ESG impacts'
];

const allSystems = [
  'RM tools', 'PLM', 'ALM', 'Compliance DB', 'CAD/CAE', 'MBSE tools', 'CAE',
  'HPC', 'SDM', 'ERP', 'MES', 'Configurator', 'SCM tools', 'SRM', 'CRM', 'EAM',
  'IoT platform', 'Data lake', 'LCA', 'ESG dashboards', 'BI tools', 'KM systems',
  'AI/ML repos', 'IAM', 'Supplier portals', 'PPM tools', 'HPC schedulers', 'LMS',
  'UX tools', 'SCADA', 'QMS', 'TMS', 'WMS', 'DMS', 'BPM', 'MDM', 'APM'
];


// Enhanced Quantum Optimization Engine
class QuantumOptimizationEngine {
  constructor() {
    this.quantumStates = new Map();
    this.optimizationHistory = [];
    this.realTimeMetrics = {
      coherence: 75,
      entanglement: 68,
      superposition: 82,
      interference: 71
    };
    this.quantumEvents = [];
    this.optimizationCycles = 0;
  }

  initializeSession(threads) {
    try {
      const sessionId = `quantum_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const session = {
        id: sessionId,
        threads: threads || [],
        startTime: new Date(),
        metrics: this.calculateInitialMetrics(threads),
        optimizations: [],
        state: 'active',
        performance: {
          startTime: performance.now(),
          frameCount: 0,
          averageFPS: 60
        }
      };
      
      this.quantumStates.set(sessionId, session);
      this.startRealTimeMonitoring(sessionId);
      this.startPerformanceMonitoring(sessionId);
      return session;
    } catch (error) {
      console.warn('Quantum session initialization warning:', error);
      return this.createFallbackSession();
    }
  }

  createFallbackSession() {
    return {
      id: 'fallback-session',
      threads: [],
      startTime: new Date(),
      metrics: {
        quantumCoherence: 50,
        valueEntanglement: 50,
        digitalSuperposition: 50,
        optimizationInterference: 50,
        overallScore: 50
      },
      optimizations: [],
      state: 'fallback'
    };
  }

  calculateInitialMetrics(threads) {
    if (!threads || !Array.isArray(threads) || threads.length === 0) {
      return {
        quantumCoherence: 50,
        valueEntanglement: 50,
        digitalSuperposition: 50,
        optimizationInterference: 50,
        overallScore: 50
      };
    }

    try {
      const coherence = this.calculateCoherence(threads);
      const entanglement = this.calculateEntanglement(threads);
      const superposition = this.calculateSuperposition(threads);
      const interference = this.calculateInterference(threads);
      const overallScore = (coherence + entanglement + superposition + interference) / 4;

      return {
        quantumCoherence: coherence,
        valueEntanglement: entanglement,
        digitalSuperposition: superposition,
        optimizationInterference: interference,
        overallScore: Math.round(overallScore * 100) / 100
      };
    } catch (error) {
      console.warn('Metrics calculation warning:', error);
      return {
        quantumCoherence: 50,
        valueEntanglement: 50,
        digitalSuperposition: 50,
        optimizationInterference: 50,
        overallScore: 50
      };
    }
  }

  calculateCoherence(threads) {
    if (!threads || threads.length === 0) return 50;
    try {
      const connections = threads.reduce((acc, thread) => acc + (thread.connections || 0), 0);
      return Math.min(100, Math.max(0, (connections / threads.length) * 20));
    } catch (error) {
      return 50;
    }
  }

  calculateEntanglement(threads) {
    if (!threads || threads.length === 0) return 50;
    try {
      const valueStreams = threads.filter(t => (t.valuePotential || 0) > 50).length;
      return Math.min(100, Math.max(0, (valueStreams / threads.length) * 100));
    } catch (error) {
      return 50;
    }
  }

  calculateSuperposition(threads) {
    if (!threads || threads.length === 0) return 50;
    try {
      const multiDomainThreads = threads.filter(t => t.domains && Array.isArray(t.domains) && t.domains.length > 2).length;
      return Math.min(100, Math.max(0, (multiDomainThreads / threads.length) * 100));
    } catch (error) {
      return 50;
    }
  }

  calculateInterference(threads) {
    if (!threads || threads.length === 0) return 50;
    try {
      const optimizedThreads = threads.filter(t => (t.optimizationPotential || 0) > 70).length;
      return Math.min(100, Math.max(0, (optimizedThreads / threads.length) * 100));
    } catch (error) {
      return 50;
    }
  }

  startRealTimeMonitoring(sessionId) {
    try {
      const interval = setInterval(() => {
        const session = this.quantumStates.get(sessionId);
        if (session && session.state === 'active') {
          this.updateRealTimeMetrics(session);
          this.detectQuantumEvents(session);
          this.optimizationCycles++;
        } else {
          clearInterval(interval);
        }
      }, 90000);
    } catch (error) {
      console.warn('Quantum monitoring warning:', error);
    }
  }

  startPerformanceMonitoring(sessionId) {
    try {
      const interval = setInterval(() => {
        const session = this.quantumStates.get(sessionId);
        if (session && session.performance) {
          const now = performance.now();
          const elapsed = now - session.performance.startTime;
          session.performance.averageFPS = (session.performance.frameCount / elapsed) * 1000;
          session.performance.frameCount = 0;
          session.performance.startTime = now;
        } else {
          clearInterval(interval);
        }
      }, 5000);
    } catch (error) {
      console.warn('Performance monitoring warning:', error);
    }
  }

  updateRealTimeMetrics(session) {
    try {
      Object.keys(this.realTimeMetrics).forEach(key => {
        const change = (Math.random() - 0.5) * 8;
        this.realTimeMetrics[key] = Math.min(95, Math.max(5, this.realTimeMetrics[key] + change));
      });

      // Sync with session metrics occasionally
      if (this.optimizationCycles % 10 === 0) {
        session.metrics = this.calculateInitialMetrics(session.threads);
      }
    } catch (error) {
      console.warn('Real-time metrics update warning:', error);
    }
  }

  detectQuantumEvents(session) {
    try {
      const events = [];
      
      if (this.realTimeMetrics.coherence > 85) {
        events.push({
          type: 'quantum_coherence_peak',
          message: 'High quantum coherence detected - optimization potential maximized',
          timestamp: new Date(),
          impact: 'positive',
          intensity: 0.8,
          metricsSnapshot: { ...this.realTimeMetrics }
        });
      }

      if (this.realTimeMetrics.entanglement > 80) {
        events.push({
          type: 'value_entanglement_surge',
          message: 'Value stream entanglement surge - cross-domain optimization active',
          timestamp: new Date(),
          impact: 'positive',
          intensity: 0.7,
          metricsSnapshot: { ...this.realTimeMetrics }
        });
      }

      if (this.realTimeMetrics.coherence < 30) {
        events.push({
          type: 'quantum_decoherence',
          message: 'Quantum decoherence detected - optimization efficiency reduced',
          timestamp: new Date(),
          impact: 'negative',
          intensity: 0.6,
          metricsSnapshot: { ...this.realTimeMetrics }
        });
      }

      if (events.length > 0) {
        this.quantumEvents.push(...events);
        session.optimizations.push(...events);
      }

      return events;
    } catch (error) {
      console.warn('Quantum event detection warning:', error);
      return [];
    }
  }

  getOptimizationRecommendations(threads) {
    const recommendations = [];
    
    if (!threads || threads.length === 0) return recommendations;
    
    try {
      threads.forEach(thread => {
        if ((thread.healthScore || 0) < 70) {
          recommendations.push({
            threadId: thread.id,
            threadName: thread.name,
            type: 'health_optimization',
            priority: 'high',
            message: `Health score low (${thread.healthScore}%) - recommend optimization`,
            actions: ['Review connections', 'Check data flow', 'Optimize performance'],
            estimatedImpact: 0.8,
            confidence: 0.9
          });
        }
        
        if ((thread.optimizationPotential || 0) > 80) {
          recommendations.push({
            threadId: thread.id,
            threadName: thread.name,
            type: 'high_potential',
            priority: 'medium',
            message: `High optimization potential (${thread.optimizationPotential}%)`,
            actions: ['Consider golden thread status', 'Review integration points', 'Analyze performance metrics'],
            estimatedImpact: 0.6,
            confidence: 0.75
          });
        }

        if (thread.connections && thread.connections < 3) {
          recommendations.push({
            threadId: thread.id,
            threadName: thread.name,
            type: 'connection_optimization',
            priority: 'medium',
            message: `Low connection count (${thread.connections}) - consider expanding integration`,
            actions: ['Identify integration points', 'Review domain coverage', 'Analyze system connections'],
            estimatedImpact: 0.5,
            confidence: 0.8
          });
        }
      });

      // Sort by priority and impact
      return recommendations.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0) || 
               b.estimatedImpact - a.estimatedImpact;
      });
    } catch (error) {
      console.warn('Optimization recommendations warning:', error);
      return [];
    }
  }

  getSessionAnalytics(sessionId) {
    try {
      const session = this.quantumStates.get(sessionId);
      if (!session) return null;

      const duration = new Date() - session.startTime;
      const recommendations = this.getOptimizationRecommendations(session.threads);
      const recentEvents = this.quantumEvents.slice(-5);
      
      return {
        sessionId: sessionId,
        duration: duration,
        threadCount: session.threads.length,
        currentMetrics: session.metrics,
        realTimeMetrics: { ...this.realTimeMetrics },
        optimizationCount: session.optimizations.length,
        recommendationCount: recommendations.length,
        recentEvents: recentEvents,
        performance: session.performance ? { ...session.performance } : null,
        optimizationCycles: this.optimizationCycles,
        overallHealth: this.calculateSystemHealth(session.threads)
      };
    } catch (error) {
      console.warn('Session analytics warning:', error);
      return null;
    }
  }

  calculateSystemHealth(threads) {
    if (!threads || threads.length === 0) return 50;
    try {
      const totalHealth = threads.reduce((sum, thread) => sum + (thread.healthScore || 50), 0);
      return Math.round(totalHealth / threads.length);
    } catch (error) {
      return 50;
    }
  }

  optimizeThread(threadId, optimizationType) {
    try {
      const optimization = {
        id: `opt_${Date.now()}`,
        threadId,
        type: optimizationType,
        timestamp: new Date(),
        status: 'applied',
        impact: 0.7
      };

      this.optimizationHistory.push(optimization);
      return optimization;
    } catch (error) {
      console.warn('Thread optimization warning:', error);
      return null;
    }
  }
}

// Enhanced Tooltip System
class AdvancedTooltipManager {
  constructor() {
    this.tooltips = new Map();
    this.interactionStats = {
      totalInteractions: 0,
      elementInteractions: new Map(),
      sessionStart: new Date(),
      popularElements: [],
      interactionFrequency: new Map()
    };
    this.tooltipHistory = [];
    this.maxHistorySize = 100;
  }

  registerTooltip(elementId, content, elementData = null, options = {}) {
    try {
      this.tooltips.set(elementId, {
        content,
        elementData,
        timestamp: Date.now(),
        interactions: 0,
        options: {
          persistent: false,
          showMetrics: true,
          enhancedStyling: true,
          ...options
        }
      });
    } catch (error) {
      console.warn('Tooltip registration warning:', error);
    }
  }

  getTooltipContent(elementId, enhanceWithData = true) {
    try {
      const tooltip = this.tooltips.get(elementId);
      if (!tooltip) return null;

      tooltip.interactions++;
      this.interactionStats.totalInteractions++;
      
      // Update interaction frequency
      const frequency = this.interactionStats.interactionFrequency.get(elementId) || 0;
      this.interactionStats.interactionFrequency.set(elementId, frequency + 1);

      let content = tooltip.content;
      if (enhanceWithData && tooltip.elementData && tooltip.options.showMetrics) {
        content = this.enhanceContentWithData(content, tooltip.elementData, tooltip.options);
      }

      // Add to history
      this.addToHistory(elementId, content);

      return content;
    } catch (error) {
      console.warn('Tooltip content retrieval warning:', error);
      return null;
    }
  }
  
  enhanceContentWithData(baseContent, elementData, options) {
    try {
      let enhancedContent = baseContent;
      
      if (elementData.healthScore !== undefined) {
        const healthColor = this.getHealthColor(elementData.healthScore);
        const healthIcon = this.getHealthIcon(elementData.healthScore);
        enhancedContent += `<br><strong>${healthIcon} Health:</strong> <span style="color: ${healthColor}">${elementData.healthScore}%</span>`;
      }
      
      if (elementData.optimizationPotential !== undefined) {
        const potentialLevel = this.getPotentialLevel(elementData.optimizationPotential);
        enhancedContent += `<br><strong>🔧 Optimization:</strong> <span style="color: ${potentialLevel.color}">${elementData.optimizationPotential}%</span> ${potentialLevel.label}`;
      }
      
      if (elementData.connections !== undefined) {
        enhancedContent += `<br><strong>🔗 Connections:</strong> ${elementData.connections}`;
      }
      
      if (elementData.domains && Array.isArray(elementData.domains)) {
        enhancedContent += `<br><strong>🌐 Domains:</strong> ${elementData.domains.join(', ')}`;
      }
      
      if (elementData.systems && Array.isArray(elementData.systems)) {
        enhancedContent += `<br><strong>💻 Systems:</strong> ${elementData.systems.join(', ')}`;
      }

      if (elementData.speed !== undefined) {
        enhancedContent += `<br><strong>⚡ Speed:</strong> ${elementData.speed.toFixed(4)}`;
      }

      if (options.enhancedStyling) {
        enhancedContent = this.applyEnhancedStyling(enhancedContent, elementData);
      }

      return enhancedContent;
    } catch (error) {
      console.warn('Content enhancement warning:', error);
      return baseContent;
    }
  }

  getHealthColor(score) {
    if (score >= 80) return '#00ff00';
    if (score >= 60) return '#ffff00';
    return '#ff0000';
  }

  getHealthIcon(score) {
    if (score >= 80) return '💚';
    if (score >= 60) return '💛';
    return '❤️';
  }

  getPotentialLevel(potential) {
    if (potential >= 80) return { color: '#00ff00', label: '(High)' };
    if (potential >= 60) return { color: '#ffff00', label: '(Medium)' };
    return { color: '#ff4444', label: '(Low)' };
  }

  applyEnhancedStyling(content, elementData) {
    return `
      <div class="enhanced-tooltip-content">
        <div class="tooltip-header">
          ${content.split('<br>')[0]}
        </div>
        <div class="tooltip-body">
          ${content.split('<br>').slice(1).join('<br>')}
        </div>
        ${elementData.healthScore ? `
        <div class="tooltip-footer">
          <div class="health-bar">
            <div class="health-fill" style="width: ${elementData.healthScore}%"></div>
          </div>
        </div>
        ` : ''}
      </div>
    `;
  }

  addToHistory(elementId, content) {
    const historyEntry = {
      elementId,
      content,
      timestamp: new Date(),
      interactionCount: this.interactionStats.interactionFrequency.get(elementId) || 0
    };

    this.tooltipHistory.push(historyEntry);
    
    // Maintain history size
    if (this.tooltipHistory.length > this.maxHistorySize) {
      this.tooltipHistory.shift();
    }

    // Update popular elements
    this.updatePopularElements();
  }

  updatePopularElements() {
    const sortedElements = Array.from(this.interactionStats.interactionFrequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([elementId, count]) => ({ elementId, count }));

    this.interactionStats.popularElements = sortedElements;
  }

  getInteractionAnalytics() {
    return {
      ...this.interactionStats,
      sessionDuration: Date.now() - this.interactionStats.sessionStart,
      totalUniqueElements: this.tooltips.size,
      averageInteractionsPerElement: this.interactionStats.totalInteractions / Math.max(this.tooltips.size, 1),
      historySize: this.tooltipHistory.length
    };
  }

  getPopularElements(limit = 5) {
    return this.interactionStats.popularElements.slice(0, limit);
  }

  clearHistory() {
    this.tooltipHistory = [];
    this.interactionStats.interactionFrequency.clear();
    this.interactionStats.popularElements = [];
  }
}

// Performance Monitor
class PerformanceMonitor {
  constructor() {
    this.metrics = {
      fps: 0,
      frameTime: 0,
      memory: 0,
      drawCalls: 0,
      triangles: 0
    };
    this.history = {
      fps: [],
      frameTime: [],
      memory: []
    };
    this.maxHistorySize = 60;
    this.lastFrameTime = performance.now();
    this.frameCount = 0;
  }

  startMonitoring() {
    this.monitoringInterval = setInterval(() => {
      this.updatePerformanceMetrics();
    }, 100000);
  }

  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }
  }

  updatePerformanceMetrics() {
    const now = performance.now();
    const delta = now - this.lastFrameTime;
    
    this.metrics.fps = Math.round(1000 / delta);
    this.metrics.frameTime = delta;
    
    // Add to history
    this.addToHistory('fps', this.metrics.fps);
    this.addToHistory('frameTime', this.metrics.frameTime);
    
    this.lastFrameTime = now;
    this.frameCount++;
  }

  addToHistory(metric, value) {
    if (!this.history[metric]) this.history[metric] = [];
    
    this.history[metric].push(value);
    
    if (this.history[metric].length > this.maxHistorySize) {
      this.history[metric].shift();
    }
  }

  getPerformanceReport() {
    return {
      current: { ...this.metrics },
      averages: {
        fps: this.calculateAverage('fps'),
        frameTime: this.calculateAverage('frameTime')
      },
      min: {
        fps: Math.min(...this.history.fps),
        frameTime: Math.max(...this.history.frameTime)
      },
      max: {
        fps: Math.max(...this.history.fps),
        frameTime: Math.min(...this.history.frameTime)
      }
    };
  }

  calculateAverage(metric) {
    const values = this.history[metric];
    if (!values || values.length === 0) return 0;
    
    const sum = values.reduce((a, b) => a + b, 0);
    return Math.round(sum / values.length);
  }
}

// Error Boundary Component
class VisualizationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      recoveryAttempts: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { 
      hasError: true, 
      error,
      recoveryAttempts: 0
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Quantum Visualization Error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Log error to analytics service
    this.logError(error, errorInfo);
  }

  logError(error, errorInfo) {
    const errorData = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // In a real app, send this to your error tracking service
    console.error('Error logged:', errorData);
  }

  handleRecovery = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: prevState.recoveryAttempts + 1
    }));
  };

handleHardReset = async () => {
  console.log('⚡ Full Quantum Reset initiated from Error Boundary');
  
  // Show resetting state
  this.setState({ 
    isResetting: true,
    resetProgress: 0 
  });
  
  try {
    // Use the robust recovery function
    if (window.performQuantumReset) {
      const success = await window.performQuantumReset();
      
      if (success) {
        this.setState({
          hasError: false,
          error: null,
          errorInfo: null,
          isResetting: false,
          resetProgress: 100
        });
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          this.setState({ resetProgress: 0 });
        }, 3000);
      } else {
        throw new Error('Quantum reset failed');
      }
    } else {
      // Fallback to page reload
      window.location.reload();
    }
  } catch (error) {
    console.error('Quantum reset failed:', error);
    this.setState({
      hasError: true,
      error: new Error('Reset failed: ' + error.message),
      isResetting: false
    });
  }
};


  render() {
    if (this.state.hasError) {
      return (
        <div className="quantum-error-fallback">
          <div className="error-content">
            <div className="error-header">
              <h2>🌌 Quantum Visualization Engine Recovery</h2>
              <div className="error-emoji">🚨</div>
            </div>
            
            <div className="error-details">
              <p><strong>What happened:</strong> The quantum graphics engine encountered an unexpected issue</p>
              <p><strong>Error:</strong> {this.state.error?.message || 'Unknown error'}</p>
              <p><strong>Recovery attempts:</strong> {this.state.recoveryAttempts}</p>
              
              {this.state.recoveryAttempts > 2 && (
                <div className="error-warning">
                  <p>⚠️ Multiple recovery attempts failed. Consider a full reset.</p>
                </div>
              )}
            </div>

            <div className="recovery-actions">
              <button 
                onClick={this.handleRecovery}
                className="recovery-button primary"
                disabled={this.state.recoveryAttempts > 3}
              >
                {this.state.recoveryAttempts > 3 ? '🔄 Maximum Recovery Attempts Reached' : '🔧 Attempt Recovery'}
              </button>
              
              <button 
                onClick={this.handleHardReset}
                className="recovery-button secondary"
              >
                ⚡ Full Quantum Reset
              </button>
            </div>

            <div className="error-suggestions">
              <h4>Suggested Actions:</h4>
              <ul>
                <li>Check your browser's WebGL support</li>
                <li>Ensure sufficient system resources</li>
                <li>Try refreshing the page</li>
                <li>Contact support if issues persist</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main Visualization Component
const UltimateDigitalThreadVisualization = () => {
  // Refs for Three.js
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const composerRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationRef = useRef(null);
  
  
  const [selectedThreads, setSelectedThreads] = useState(['thread1', 'thread2']);
  const [isGoldenMode, setIsGoldenMode] = useState(false);
  //const sceneRef = useRef();
  //const threadsRef = useRef(new Map());
  
  // Enhanced state management
  const [state, setState] = useState({
    // Visualization states
	currentLayout: 'default',
    isTransformingLayout: false,
    isPlaying: true,
    flowSpeed: 1,
    rotationSpeed: 0.5,
    glowIntensity: 1.5,
    bloomStrength: 1.2,
    
    // View states
    currentView: 'quantum',
    showLabels: true,
    showParticles: true,
    showConnections: true,
    showWorkflow: true,
    zoomLevel: 1,
    
	
	// Thread management
isThreadControlVisible: true, // ADD THIS
visibleThreads: new Set(DIGITAL_THREADS.map(thread => thread.id)),
highlightedThread: null,
goldenThreads: new Set([1, 3, 7, 13, 22]),
/*
    // Thread management - ENHANCED
    //selectedThreads: new Set(),
    visibleThreads: new Set(DIGITAL_THREADS.map(thread => thread.id)), // Start with all threads visible
    highlightedThread: null,
    goldenThreads: new Set([1, 3, 7, 13, 22]), // More golden threads
*/    
    // NEW: Filter management
    filterMode: 'all', // 'all', 'connected', 'selected'
    showOnlyConnectedEntities: true,

      // NEW: Add these properties
  isThreadControlVisible: true,
  isLayoutPanelVisible: true,
  selectedThreads: new Set(), // Already exists, but ensure it's a Set
  threadControlPanel: {
    isMinimized: false,
    isDocked: true,
    isCollapsed: false,
  },
	
    // UI States
    showControls: true,
    showThreadPanel: true,
    showQuantumPanel: false,
    showAnalyticsPanel: false,
    showPerformancePanel: false,
    isFullScreen: false,
    currentSelection: null,
    
    // Tooltip state
    tooltip: {
      visible: false,
      content: '',
      x: 0,
      y: 0,
      elementData: null,
      type: 'default'
    },
    
    // Quantum states
    quantumMetrics: {
      coherence: 75,
      entanglement: 68,
      superposition: 82,
      interference: 71
    },
    quantumEvents: [],
    
    // Analytics
    interactionStats: {
      totalInteractions: 0,
      popularElements: [],
      sessionStart: new Date()
    },
    
    // Performance
    performance: {
      fps: 60,
      frameTime: 0,
      memory: 0
    },

    expandedSections: {
    bulkManagement: true,
    quantumControls: true,
    quantumMetrics: true,
    threadManagement: true, // This will remain expanded by default
    quantumNavigation: true,
	 layoutManagement: true // ADD THIS
  },

    // Error states
    hasError: false,
    errorMessage: '',
    isLoading: true,
    loadingProgress: 0,
    
    // System states
    isInitialized: false,
    animationPhase: 'entrance',
    lastFrameTime: performance.now(),
    
    // Configuration
    autoRotate: true,
    enableEffects: true,
    highQuality: true,
    debugMode: false,
  });




  // Refs for 3D objects and systems
  const departmentsRef = useRef(new Map());
  const threadsRef = useRef(new Map());
  const particlesRef = useRef([]);
  const tooltipManagerRef = useRef(new AdvancedTooltipManager());
  const quantumEngineRef = useRef(new QuantumOptimizationEngine());
  const performanceMonitorRef = useRef(new PerformanceMonitor());
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const eventListenersRef = useRef([]);
  const resizeObserverRef = useRef(null);



  // THEN define the state
  const [cameraState, setCameraState] = useState({
    zoomLevel: 1,
    targetPosition: new THREE.Vector3(0, 0, 0),
    isAnimating: false,
    lastInteraction: Date.now()
  });

  
  // Enhanced Entity type colors with better theming
  const entityTypeColors = useMemo(() => ({
    function: 0xff6b6b,    // Vibrant Red
    domain: 0x4ecdc4,      // Teal
    system: 0xffa500,      // Orange
    custom: 0x45b7d1,      // Blue
    golden: 0xffd700,      // Gold
    quantum: 0x9370db,     // Purple for quantum effects
    selected: 0x00ff00,    // Green for selected items
    highlighted: 0xff00ff,  // Magenta for highlighted
    warning: 0xff4444,     // Red for warnings
    success: 0x00ff88      // Green for success
  }), []);

  // Text colors matching entity types
  const entityTextColors = useMemo(() => ({
    function: '#ff6b6b',
    domain: '#4ecdc4', 
    system: '#ffa500',
    custom: '#45b7d1',
    golden: '#ffd700',
    quantum: '#9370db',
    selected: '#00ff00',
    highlighted: '#ff00ff'
  }), []);


// NEW: Generate departments data from imported threads and defined arrays
  const departmentsData = useMemo(() => {
    const departments = [];
    
    // Generate Function departments
    allFunctions.forEach((func, index) => {
      const angle = (index / allFunctions.length) * Math.PI * 2;
      const radius = 25;
      departments.push({
        id: `func-${func.toLowerCase().replace(/\s+/g, '-')}`,
        name: func,
        type: 'function',
        position: [
          radius * Math.cos(angle),
          5 * Math.sin(angle * 2),
          radius * Math.sin(angle)
        ],
        layer: 'function',
        description: `${func} department`,
        connections: Math.floor(Math.random() * 10) + 5,
        healthScore: Math.floor(Math.random() * 30) + 70
      });
    });

    // Generate Domain departments
    allDomains.forEach((domain, index) => {
      const angle = (index / allDomains.length) * Math.PI * 2;
      const radius = 45;
      departments.push({
        id: `domain-${domain.toLowerCase().replace(/\s+/g, '-')}`,
        name: domain,
        type: 'domain',
        position: [
          radius * Math.cos(angle),
          8 * Math.sin(angle * 2),
          radius * Math.sin(angle)
        ],
        layer: 'domain',
        description: `${domain} domain`,
        connections: Math.floor(Math.random() * 15) + 8,
        healthScore: Math.floor(Math.random() * 30) + 70
      });
    });

    // Generate System departments
    allSystems.forEach((system, index) => {
      const angle = (index / allSystems.length) * Math.PI * 2;
      const radius = 65;
      departments.push({
        id: `system-${system.toLowerCase().replace(/\s+/g, '-')}`,
        name: system,
        type: 'system',
        position: [
          radius * Math.cos(angle),
          10 * Math.sin(angle * 2),
          radius * Math.sin(angle)
        ],
        layer: 'system',
        description: `${system} system`,
        connections: Math.floor(Math.random() * 20) + 10,
        healthScore: Math.floor(Math.random() * 30) + 70
      });
    });

    return departments;
  }, []);


 // Use imported digital threads data with enhanced properties
  const threadsData = useMemo(() => {
    return DIGITAL_THREADS.map(thread => ({
      ...thread,
      healthScore: Math.floor(Math.random() * 30) + 70,
      optimizationPotential: Math.floor(Math.random() * 40) + 60,
      connections: thread.connects.length + thread.domains.length + thread.systems.length,
      valueStream: Math.random() > 0.3,
      critical: Math.random() > 0.5,
      performance: {
        latency: Math.floor(Math.random() * 200) + 50,
        throughput: Math.floor(Math.random() * 500) + 100,
        reliability: Math.floor(Math.random() * 5) + 95
      }
    }));
  }, []);

    // Initialize layout transformer with required dependencies
  const layoutDependencies = {
    sceneRef,
    departmentsRef,
    threadsRef,
    departmentsData,
    threadsData
    // Add any other required dependencies
  };

/*
// ADD THIS near other function declarations (around line 4500)
const transformToLayout = useCallback((layoutId) => {
  safeOperation(() => {
    console.log(`Transforming to layout: ${layoutId}`);
    // Implementation will be added from the transformer file
    return Promise.resolve(true);
  }, false, 'transformToLayout');
}, [safeOperation]);



const resetToDefaultLayout = useCallback(() => {
  safeOperation(() => {
    console.log('Resetting to default layout');
    // Implementation will be added from the transformer file
    return Promise.resolve(true);
  }, false, 'resetToDefaultLayout');
}, [safeOperation]);


const setAsDefaultLayout = useCallback(() => {
  safeOperation(() => {
    console.log('Setting current layout as default');
    // Implementation will be added from the transformer file
    return Promise.resolve(true);
  }, false, 'setAsDefaultLayout');
}, [safeOperation]);


const availableLayouts = useMemo(() => ({
  default: { name: 'Default', description: 'Original layout', icon: '🏠', complexity: 0.5 },
  optimized: { name: 'Optimized', description: 'Shortest path layout', icon: '⚡', complexity: 0.8 },
  galacticSpiral: { name: 'Galactic', description: 'Spiral layout', icon: '🌌', complexity: 0.7 },
  neuralNetwork: { name: 'Neural', description: 'Network layout', icon: '🧠', complexity: 0.9 }
}), []);

*/




  
// FIXED: Calculate which entities are connected to visible threads
const getConnectedEntities = useCallback(() => {
  const connectedFunctions = new Set();
  const connectedDomains = new Set();
  const connectedSystems = new Set();

  threadsData.forEach(thread => {
    if (state.visibleThreads.has(thread.id)) {
      // Add all connected functions, domains, and systems
      thread.connects?.forEach(func => connectedFunctions.add(func));
      thread.domains?.forEach(domain => connectedDomains.add(domain));
      thread.systems?.forEach(system => connectedSystems.add(system));
    }
  });

  return {
    functions: connectedFunctions,
    domains: connectedDomains,
    systems: connectedSystems
  };
}, [state.visibleThreads, threadsData]);

  // NEW: Enhanced department visibility based on connected threads
// FIXED: Enhanced department visibility based on connected threads
const updateDepartmentVisibility = useCallback(() => {
  const connectedEntities = getConnectedEntities();
  
  departmentsRef.current.forEach((dept, deptId) => {
    let shouldBeVisible = true;
    
    // Only apply filtering if the option is enabled AND we have visible threads
    if (state.showOnlyConnectedEntities && state.visibleThreads.size > 0) {
      const deptData = dept.userData?.departmentData || dept.userData;
      
      if (deptData) {
        if (deptData.type === 'function') {
          shouldBeVisible = connectedEntities.functions.has(deptData.name);
        } else if (deptData.type === 'domain') {
          shouldBeVisible = connectedEntities.domains.has(deptData.name);
        } else if (deptData.type === 'system') {
          shouldBeVisible = connectedEntities.systems.has(deptData.name);
        }
      }
    }
    
    // Always show departments if no filtering or no threads visible
    if (state.visibleThreads.size === 0 && state.showOnlyConnectedEntities) {
      shouldBeVisible = false; // Hide all if no threads visible and filtering is on
    } else if (state.visibleThreads.size === 0) {
      shouldBeVisible = true; // Show all if no threads visible but filtering is off
    }
    
    dept.visible = shouldBeVisible;
    
    // Also hide labels if department is hidden
    const label = dept.children.find(child => child.userData?.isLabel);
    if (label) {
      label.visible = shouldBeVisible && state.showLabels;
    }
  });
}, [state.showOnlyConnectedEntities, state.showLabels, state.visibleThreads, getConnectedEntities]);

  // Safe state update with error handling
  const safeSetState = useCallback((update) => {
    try {
      setState(prev => {
        if (typeof update === 'function') {
          return update(prev);
        }
        return { ...prev, ...update };
      });
    } catch (error) {
      console.error('State update error:', error);
      setState(prev => ({ 
        ...prev, 
        hasError: true, 
        errorMessage: 'State update failed' 
      }));
    }
  }, []);

  // Enhanced safe operation wrapper with logging
  const safeOperation = useCallback((operation, fallback = null, operationName = 'unknown') => {
    try {
      const result = operation();
      return result;
    } catch (error) {
      console.warn(`Operation "${operationName}" failed:`, error);
      
      // Log operation failure for analytics
      if (window.trackEvent) {
        window.trackEvent('operation_error', {
          operation: operationName,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
      
      return fallback;
    }
  }, []);

  //const { currentLayout, isTransforming } = useQuantumLayoutTransformer(layoutDependencies);
    const {
  currentLayout,
  isTransforming,
  transformToLayout,
  resetToDefaultLayout,
  setAsDefaultLayout,
  availableLayouts
  } = useQuantumLayoutTransformer ({
  ...layoutDependencies,
  selectedThreads: Array.from(state.selectedThreads),
  safeOperation
});

      // Sync layout transformer with main state
  useEffect(() => {
    safeSetState(prev => ({
      ...prev,
      currentLayout,
      isTransformingLayout: isTransforming
    }));
  }, [currentLayout, isTransforming]);
  
// Create Quantum Bloom Effect for enhanced visual appeal
const createQuantumBloomEffect = useCallback((intensity = 2.0, duration = 1000) => {
  return safeOperation(() => {
    if (!composerRef.current) return;

    const bloomPass = composerRef.current.passes.find(pass => 
      pass instanceof UnrealBloomPass
    );

    if (!bloomPass) return;

    const originalStrength = bloomPass.strength;
    const originalRadius = bloomPass.radius;
    
    // Enhanced bloom settings
    bloomPass.strength = intensity;
    bloomPass.radius = 0.8;
    
    console.log(`Quantum bloom effect activated: ${intensity}x intensity`);

    // Animate bloom effect
    const startTime = Date.now();
    const animateBloom = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out animation
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      bloomPass.strength = originalStrength + (intensity - originalStrength) * (1 - easeProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateBloom);
      } else {
        // Restore original settings
        bloomPass.strength = originalStrength;
        bloomPass.radius = originalRadius;
      }
    };

    animateBloom();
    
  }, null, 'createQuantumBloomEffect');
}, [safeOperation]);


  
  // NEW: Bulk thread visibility controls
// ENHANCED THREAD VISIBILITY CONTROLS
const showAllThreads = useCallback(() => {
  safeSetState(prev => {
    const newVisibleThreads = new Set(threadsData.map(t => t.id));
    
    // Update all thread visibilities
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.visible = true;
      }
    });
    
    // Update department visibility
    updateDepartmentVisibility();
    
    // Create visual feedback
    //createQuantumSurge(0.5);
    
    return {
      ...prev,
      visibleThreads: newVisibleThreads
    };
  });
}, [threadsData, updateDepartmentVisibility, safeSetState]);

const hideAllThreads = useCallback(() => {
  safeSetState(prev => {
    // Hide all threads
    threadsRef.current.forEach((threadObj) => {
      if (threadObj?.group) {
        threadObj.group.visible = false;
      }
    });
    
    // Update department visibility
    updateDepartmentVisibility();
    
    // Create visual feedback
    createQuantumBloomEffect(1.2, 500);
    
    return {
      ...prev,
      visibleThreads: new Set()
    };
  });
}, [updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);

const showOnlyGoldenThreads = useCallback(() => {
  safeSetState(prev => {
    const newVisibleThreads = new Set(state.goldenThreads);
    
    // Update thread visibilities based on golden status
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.visible = state.goldenThreads.has(threadId);
      }
    });
    
    // Update department visibility
    updateDepartmentVisibility();
    
    
    return {
      ...prev,
      visibleThreads: newVisibleThreads
    };
  });
}, [state.goldenThreads, updateDepartmentVisibility, safeSetState]);

const showOnlyCriticalThreads = useCallback(() => {
  safeSetState(prev => {
    const criticalThreads = threadsData.filter(thread => thread.critical).map(t => t.id);
    const newVisibleThreads = new Set(criticalThreads);
    
    // Update thread visibilities based on critical status
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.visible = criticalThreads.includes(threadId);
      }
    });
    
    // Update department visibility
    updateDepartmentVisibility();
    
    // Create critical visual feedback
    //createQuantumSurge(0.8);
    
    return {
      ...prev,
      visibleThreads: newVisibleThreads
    };
  });
}, [threadsData, updateDepartmentVisibility, safeSetState]);


const selectAllThreads = useCallback(() => {
  safeSetState(prev => {
    const newSelectedThreads = new Set(threadsData.map(t => t.id));
    
    // Update all thread appearances
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'thread-connection' && child.material) {
            child.material.color.setHex(entityTypeColors.selected);
            child.material.opacity = 0.9;
            if (child.material.linewidth !== undefined) {
              child.material.linewidth = 4;
            }
            child.material.needsUpdate = true;
          }
        });
      }
    });
    
    return {
      ...prev,
      selectedThreads: newSelectedThreads
    };
  });
}, [threadsData, entityTypeColors, safeSetState]);


const deselectAllThreads = useCallback(() => {
  safeSetState(prev => {
    // Reset all thread appearances
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'thread-connection' && child.material) {
            const originalColor = threadObj.isGolden ? 
              entityTypeColors.golden : 
              (typeof threadObj.thread.color === 'number' ? threadObj.thread.color : 0x45b7d1);
            child.material.color.setHex(originalColor);
            child.material.opacity = 0.6;
            if (child.material.linewidth !== undefined) {
              child.material.linewidth = 2;
            }
            child.material.needsUpdate = true;
          }
        });
      }
    });
    
    return {
      ...prev,
      selectedThreads: new Set()
    };
  });
}, [entityTypeColors, safeSetState]);

// FIXED: Toggle filter mode with proper state management
const toggleFilterMode = useCallback(() => {
  safeSetState(prev => ({
    ...prev,
    showOnlyConnectedEntities: !prev.showOnlyConnectedEntities
  }));
}, [safeSetState]);

// Add this useEffect to handle filter changes
useEffect(() => {
  if (state.isInitialized) {
    updateDepartmentVisibility();
  }
}, [state.showOnlyConnectedEntities, state.visibleThreads, state.isInitialized, updateDepartmentVisibility]);



  // Enhanced quantum wow moment with multiple effects
  const createQuantumWowMoment = useCallback((threadId = null) => {
    safeOperation(() => {
      if (!sceneRef.current) return;

      const particleCount = 200;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(particleCount * 3);
      const colors = new Float32Array(particleCount * 3);
      const sizes = new Float32Array(particleCount);
      
      const center = new THREE.Vector3(0, 0, 0);
      const thread = threadId ? threadsData.find(t => t.id === threadId) : null;
      const baseColor = thread ? new THREE.Color(thread.color) : new THREE.Color(0x9370db);

      // Create particle explosion
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        
        // Spherical distribution
        const phi = Math.acos(-1 + (2 * i) / particleCount);
        const theta = Math.sqrt(particleCount * Math.PI) * phi;
        
        const distance = 5 + Math.random() * 45;
        
        positions[i3] = distance * Math.sin(phi) * Math.cos(theta);
        positions[i3 + 1] = distance * Math.sin(phi) * Math.sin(theta);
        positions[i3 + 2] = distance * Math.cos(phi);
        
        // Color variation
        const color = baseColor.clone();
        const hueVariation = (Math.random() - 0.5) * 0.3;
        const saturationVariation = (Math.random() - 0.5) * 0.2;
        color.offsetHSL(hueVariation, saturationVariation, 0);
        
        colors[i3] = color.r;
        colors[i3 + 1] = color.g;
        colors[i3 + 2] = color.b;
        
        // Size variation
        sizes[i] = Math.random() * 4 + 1;
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

      const material = new THREE.PointsMaterial({
        size: 3,
        vertexColors: true,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true
      });

      const particles = new THREE.Points(geometry, material);
      particles.name = 'wow-effect-particles';
      sceneRef.current.add(particles);

      // Enhanced animation with multiple phases
      const startPositions = positions.slice();
      const startTime = Date.now();
      const duration = 2500;

      const animateParticles = () => {
        try {
          const now = Date.now();
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          const currentPositions = particles.geometry.attributes.position.array;
          const currentSizes = particles.geometry.attributes.size.array;
          
          for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            const startX = startPositions[i3];
            const startY = startPositions[i3 + 1];
            const startZ = startPositions[i3 + 2];
            
            // Multi-phase easing
            let easeProgress;
            if (progress < 0.3) {
              // Fast expansion
              easeProgress = progress / 0.3;
            } else if (progress < 0.7) {
              // Slow contraction
              easeProgress = 1 - ((progress - 0.3) / 0.4) * 0.5;
            } else {
              // Fast collapse
              easeProgress = (1 - progress) / 0.3;
            }
            
            currentPositions[i3] = startX * easeProgress;
            currentPositions[i3 + 1] = startY * easeProgress;
            currentPositions[i3 + 2] = startZ * easeProgress;
            
            // Pulsing size effect
            const pulseIntensity = 0.5 + 0.5 * Math.sin(progress * Math.PI * 8 + i * 0.1);
            currentSizes[i] = sizes[i] * pulseIntensity;
          }
          
          particles.geometry.attributes.position.needsUpdate = true;
          particles.geometry.attributes.size.needsUpdate = true;
          
          // Fade out
          material.opacity = 1 - Math.pow(progress, 2);
          
          if (progress < 1) {
            requestAnimationFrame(animateParticles);
          } else {
            if (sceneRef.current && particles.parent === sceneRef.current) {
              sceneRef.current.remove(particles);
            }
          }
        } catch (error) {
          console.warn('Quantum wow moment animation warning:', error);
        }
      };

      animateParticles();
      
      // Enhanced quantum bloom effect
      if (composerRef.current && composerRef.current.passes[1]) {
        const bloomPass = composerRef.current.passes[1];
        const originalStrength = bloomPass.strength;
        bloomPass.strength = originalStrength * 4;
        
        setTimeout(() => {
          if (bloomPass) {
            bloomPass.strength = originalStrength;
          }
        }, 1500);
      }
      
    }, null, 'createQuantumWowMoment');
  }, [threadsData, safeOperation]);



// Enhanced digital threads visualization using the new functions
// ENHANCED TEXT SCALING SYSTEM
const updateTextScaling = useCallback((zoomLevel) => {
  safeOperation(() => {
    const scaleFactor = Math.max(0.5, Math.min(3, zoomLevel * 2));
    
    // Scale department labels
    departmentsRef.current.forEach(dept => {
      const label = dept.children.find(child => child.userData?.isLabel);
      if (label && label.scale) {
        const baseScale = 18; // Match your createFloatingLabel scale
        label.scale.set(baseScale * scaleFactor, 6 * scaleFactor, 1);
      }
    });

    // Scale sequence labels
    threadsRef.current.forEach(threadObj => {
      if (threadObj.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'sequence-label' && child.scale) {
            const baseScale = 1; // Match your createSequenceLabel scale
            child.scale.set(baseScale * scaleFactor, baseScale * scaleFactor, 1);
          }
        });
      }
    });

  }, null, 'updateTextScaling');
}, [safeOperation]);


  // Define animateCameraToTarget FIRST, before any other functions that use it
// ENHANCED CAMERA ANIMATION WITH SMOOTH ZOOM
const animateCameraToTarget = useCallback((targetPosition, distance, duration = 1000) => {
  if (!cameraRef.current || !controlsRef.current) return;

  const startPosition = cameraRef.current.position.clone();
  const startTarget = controlsRef.current.target.clone();
  const endPosition = targetPosition.clone().add(
    new THREE.Vector3(distance * 0.7, distance * 0.5, distance * 0.7)
  );

  const startTime = Date.now();
  
  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Smooth easing function
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    
    cameraRef.current.position.lerpVectors(startPosition, endPosition, easeProgress);
    controlsRef.current.target.lerpVectors(startTarget, targetPosition, easeProgress);
    controlsRef.current.update();
    
    // Update zoom level during animation
    const currentDistance = cameraRef.current.position.distanceTo(targetPosition);
    const zoomLevel = Math.max(0.1, Math.min(2, 120 / currentDistance));
    safeSetState(prev => ({ ...prev, zoomLevel }));
    updateTextScaling(zoomLevel);
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };
  
  animate();
}, [updateTextScaling, safeSetState]);

// ENHANCED ZOOM LEVEL DISPLAY WITH PERFORMANCE INDICATOR
const ZoomLevelDisplay = () => {
  const getZoomClass = () => {
    if (state.zoomLevel >= 5) return 'ultra-zoom';
    if (state.zoomLevel >= 2) return 'high-zoom';
    return '';
  };

  const getReadability = () => {
    if (state.zoomLevel >= 3) return '📖 Ultra Readable';
    if (state.zoomLevel >= 1.5) return '👁️ Readable';
    if (state.zoomLevel >= 0.5) return '👀 Overview';
    return '🌐 Distant View';
  };

  return (
    <div className={`zoom-level-display ${getZoomClass()}`}>
      🔍 Zoom: {state.zoomLevel.toFixed(1)}x
      <span className="zoom-readability">{getReadability()}</span>
      {state.zoomLevel >= 3 && (
        <div className="zoom-performance">
          ⚡ Performance: {performanceMode}
        </div>
      )}
    </div>
  );
};




const zoomToEntity = useCallback((entityId, entityType, duration = 1000) => {
  safeOperation(() => {
    let targetPosition = new THREE.Vector3(0, 0, 0);
    let zoomDistance = 50;

    if (entityType === 'department') {
      const dept = departmentsRef.current.get(entityId);
      if (dept) {
        targetPosition.copy(dept.position);
        zoomDistance = 15;
      }
    } else if (entityType === 'thread') {
      // Find the thread and zoom to its midpoint
      const threadObj = threadsRef.current.get(entityId);
      if (threadObj?.group) {
        const bbox = new THREE.Box3().setFromObject(threadObj.group);
        targetPosition = bbox.getCenter(new THREE.Vector3());
        zoomDistance = 30;
      }
    } else if (entityType === 'system') {
      // System overview
      targetPosition.set(0, 0, 0);
      zoomDistance = 120;
    }

    animateCameraToTarget(targetPosition, zoomDistance, duration);
    createQuantumBloomEffect(1.5, duration);

  }, null, 'zoomToEntity');
}, [animateCameraToTarget, createQuantumBloomEffect, safeOperation]);

const resetView = useCallback(() => {
  safeOperation(() => {
    const defaultPosition = new THREE.Vector3(120, 80, 120);
    const defaultTarget = new THREE.Vector3(0, 0, 0);
    
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.copy(defaultPosition);
      controlsRef.current.target.copy(defaultTarget);
      controlsRef.current.update();
    }
    
    safeSetState(prev => ({
      ...prev,
      selectedThreads: new Set(),
      highlightedThread: null,
      zoomLevel: 1
    }));
    
    createQuantumBloomEffect(2.0, 1000);
    
  }, null, 'resetView');
}, [createQuantumBloomEffect, safeSetState, safeOperation]);



  
// Enhanced Quantum Camera Hook
// Enhanced Quantum Camera Hook - INSERT AFTER safeOperation


// Performance Optimization System
const [performanceMode, setPerformanceMode] = useState('balanced');

// ULTRA-PERFORMANCE OPTIMIZATION SYSTEM
const optimizeVisualization = useCallback((mode = performanceMode) => {
  safeOperation(() => {
    const config = {
      ultra: { // New ultra mode for 10x zoom
        particleCount: 10,
        curveSegments: 120,
        shadowQuality: false,
        bloomEnabled: true,
        labelsEnabled: true,
        geometryQuality: 48
      },
      high: {
        particleCount: 8,
        curveSegments: 80,
        shadowQuality: false,
        bloomEnabled: true,
        labelsEnabled: true,
        geometryQuality: 32
      },
      balanced: {
        particleCount: 5,
        curveSegments: 50,
        shadowQuality: false,
        bloomEnabled: true,
        labelsEnabled: true,
        geometryQuality: 24
      },
      performance: {
        particleCount: 2,
        curveSegments: 25,
        shadowQuality: false,
        bloomEnabled: false,
        labelsEnabled: false,
        geometryQuality: 16
      }
    };

    const settings = config[mode] || config.balanced;

    // Optimize particle counts
    let visibleParticles = 0;
    particlesRef.current.forEach(particle => {
      const shouldShow = visibleParticles < settings.particleCount * 10; // Scale factor
      particle.visible = shouldShow && state.showParticles;
      if (shouldShow) visibleParticles++;
    });

    // Optimize thread connection quality
    threadsRef.current.forEach(threadObj => {
      if (threadObj.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'thread-connection') {
            child.geometry = child.geometry || new THREE.BufferGeometry();
            if (child.geometry.attributes.position) {
              const points = child.userData.curve.getPoints(settings.curveSegments);
              child.geometry.setFromPoints(points);
            }
          }
        });
      }
    });

    // Optimize department geometries
    departmentsRef.current.forEach(dept => {
      if (dept.children[0]?.geometry) {
        const sphere = dept.children[0];
        if (sphere.geometry.parameters) {
          const currentSegments = sphere.geometry.parameters.widthSegments;
          if (currentSegments !== settings.geometryQuality) {
            const newGeometry = new THREE.SphereGeometry(
              sphere.geometry.parameters.radius,
              settings.geometryQuality,
              settings.geometryQuality
            );
            sphere.geometry.dispose();
            sphere.geometry = newGeometry;
          }
        }
      }
    });

    // Update bloom effects
    if (composerRef.current) {
      const bloomPass = composerRef.current.passes.find(pass => 
        pass instanceof UnrealBloomPass
      );
      if (bloomPass) {
        bloomPass.enabled = settings.bloomEnabled;
      }
    }

    console.log(`Performance optimized: ${mode} mode (${settings.curveSegments} segments)`);
    
  }, null, 'optimizeVisualization');
}, [performanceMode, state.showParticles, safeOperation]);


// Auto-optimize based on FPS
const useAutoPerformance = useCallback(() => {
  const checkPerformance = () => {
    const report = performanceMonitorRef.current.getPerformanceReport();
    
    if (report.current.fps < 30 && performanceMode !== 'performance') {
      setPerformanceMode('performance');
      optimizeVisualization('performance');
    } else if (report.current.fps > 50 && performanceMode === 'performance') {
      setPerformanceMode('balanced');
      optimizeVisualization('balanced');
    } else if (report.current.fps > 90 && performanceMode === 'balanced') {
      setPerformanceMode('high');
      optimizeVisualization('high');
    }
  };

  const interval = setInterval(checkPerformance, 5000);
  return () => clearInterval(interval);
}, [performanceMode, optimizeVisualization]);

// Level of Detail (LOD) System
const [lodLevel, setLodLevel] = useState('high');

const updateThreadLOD = useCallback((level = lodLevel) => {
  safeOperation(() => {
    const lodConfig = {
      high: { segments: 32, particles: 8, opacity: 0.8 },
      medium: { segments: 16, particles: 5, opacity: 0.6 },
      low: { segments: 8, particles: 3, opacity: 0.4 }
    };

    const config = lodConfig[level] || lodConfig.medium;

    // Update department geometries
    departmentsRef.current.forEach(dept => {
      if (dept.children[0]?.geometry) {
        const currentSegments = dept.children[0].geometry.parameters?.widthSegments;
        if (currentSegments !== config.segments) {
          const newGeometry = new THREE.SphereGeometry(
            dept.children[0].geometry.parameters.radius,
            config.segments,
            config.segments
          );
          dept.children[0].geometry.dispose();
          dept.children[0].geometry = newGeometry;
        }
      }
    });

    // Update thread opacities and particle counts
    threadsRef.current.forEach(threadObj => {
      if (threadObj.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'thread-connection' && child.material) {
            child.material.opacity = config.opacity;
            child.material.needsUpdate = true;
          }
          
          if (child.userData?.type === 'data-particle') {
            child.visible = Math.random() < (config.particles / 8);
          }
        });
      }
    });

    setLodLevel(level);
    
  }, null, 'updateThreadLOD');
}, [lodLevel, safeOperation]);

// Auto LOD based on distance from camera
const useAutoLOD = useCallback(() => {
  const updateLODBasedOnDistance = () => {
    if (!cameraRef.current) return;

    const cameraPosition = cameraRef.current.position;
    let avgDistance = 0;
    let count = 0;

    departmentsRef.current.forEach(dept => {
      const distance = dept.position.distanceTo(cameraPosition);
      avgDistance += distance;
      count++;
    });

    if (count > 0) {
      avgDistance /= count;
      
      let newLodLevel = 'high';
      if (avgDistance > 150) newLodLevel = 'low';
      else if (avgDistance > 80) newLodLevel = 'medium';

      if (newLodLevel !== lodLevel) {
        updateThreadLOD(newLodLevel);
      }
    }
  };

  const interval = setInterval(updateLODBasedOnDistance, 2000);
  return () => clearInterval(interval);
}, [lodLevel, updateThreadLOD]);

// Enhanced Interaction System
const handleEnhancedMouseClick = useCallback((event) => {
  safeOperation(() => {
    if (!cameraRef.current || !sceneRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    const intersectableObjects = [];
    departmentsRef.current.forEach(dept => intersectableObjects.push(dept));
    threadsRef.current.forEach(thread => {
      if (thread.group && thread.group.visible) {
        intersectableObjects.push(thread.group);
      }
    });

    const intersects = raycasterRef.current.intersectObjects(intersectableObjects, true);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      const object = intersect.object;
      const userData = object.userData || object.parent?.userData;

      if (userData) {
        if (userData.type === 'thread-connection') {
          for (const [threadId, threadObj] of threadsRef.current.entries()) {
            if (threadObj.group.children.includes(object) || 
                threadObj.group.children.some(child => child.children?.includes(object))) {
              
              zoomToEntity(threadId, 'thread');
              toggleThreadSelection(threadId);
              
              safeSetState(prev => ({
                ...prev,
                currentSelection: {
                  type: 'thread',
                  data: threadObj.thread
                },
                showAnalyticsPanel: true
              }));
              break;
            }
          }
        } else if (userData.type === 'department') {
          zoomToEntity(userData.id, 'department');
          
          safeSetState(prev => ({
            ...prev,
            currentSelection: {
              type: 'department',
              data: userData
            },
            showAnalyticsPanel: true
          }));
        }
        
        createQuantumWowMoment();
      }
    } else {
      safeSetState(prev => ({
        ...prev,
        currentSelection: null
      }));
    }
  }, null, 'handleEnhancedMouseClick');
}, [safeOperation]);

const handleDoubleClick = useCallback((event) => {
  safeOperation(() => {
    if (!cameraRef.current || !sceneRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    const intersectableObjects = [];
    departmentsRef.current.forEach(dept => intersectableObjects.push(dept));
    threadsRef.current.forEach(thread => {
      if (thread.group && thread.group.visible) {
        intersectableObjects.push(thread.group);
      }
    });

    const intersects = raycasterRef.current.intersectObjects(intersectableObjects, true);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      const object = intersect.object;
      const userData = object.userData || object.parent?.userData;

      if (userData) {
        if (userData.type === 'department') {
          zoomToEntity(userData.id, 'department', 600);
        } else if (userData.type === 'thread-connection') {
          for (const [threadId, threadObj] of threadsRef.current.entries()) {
            if (threadObj.group.children.includes(object) || 
                threadObj.group.children.some(child => child.children?.includes(object))) {
              zoomToEntity(threadId, 'thread', 600);
              break;
            }
          }
        }
      }
    }
  }, null, 'handleDoubleClick');
}, [ safeOperation]);

const useQuantumPerformance = () => {
  const [performanceMode, setPerformanceMode] = useState('balanced');
  
  const optimizeVisualization = useCallback((mode = performanceMode) => {
    // ... implementation from previous response
  }, [performanceMode, safeOperation, safeSetState]);

  const useAutoPerformance = useCallback(() => {
    // ... implementation from previous response
  }, [performanceMode, optimizeVisualization]);

  return {
    performanceMode,
    setPerformanceMode,
    optimizeVisualization,
    useAutoPerformance
  };
};

// Level of Detail (LOD) system for threads
const useQuantumLOD = () => {
  const [lodLevel, setLodLevel] = useState('high');
  
  const updateThreadLOD = useCallback((level = lodLevel) => {
    // ... implementation from previous response
  }, [lodLevel, safeOperation]);

  const useAutoLOD = useCallback(() => {
    // ... implementation from previous response
  }, [lodLevel, updateThreadLOD]);

  return {
    lodLevel,
    updateThreadLOD,
    useAutoLOD
  };
};

// Enhanced interaction system
const useQuantumInteractions = () => {
//  const { zoomToEntity } = useQuantumCamera();

  const handleEnhancedMouseClick = useCallback((event) => {
    // ... implementation from previous response
  }, [zoomToEntity, createQuantumWowMoment, safeSetState, safeOperation]);

  const handleDoubleClick = useCallback((event) => {
    // Enhanced double-click zoom implementation
    safeOperation(() => {
      if (!cameraRef.current || !sceneRef.current || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

      const intersectableObjects = [];
      departmentsRef.current.forEach(dept => intersectableObjects.push(dept));
      threadsRef.current.forEach(thread => {
        if (thread.group && thread.group.visible) {
          intersectableObjects.push(thread.group);
        }
      });

      const intersects = raycasterRef.current.intersectObjects(intersectableObjects, true);

      if (intersects.length > 0) {
        const intersect = intersects[0];
        const object = intersect.object;
        const userData = object.userData || object.parent?.userData;

        if (userData) {
          if (userData.type === 'department') {
            zoomToEntity(userData.id, 'department', 600);
          } else if (userData.type === 'thread-connection') {
            for (const [threadId, threadObj] of threadsRef.current.entries()) {
              if (threadObj.group.children.includes(object) || 
                  threadObj.group.children.some(child => child.children?.includes(object))) {
                zoomToEntity(threadId, 'thread', 600);
                break;
              }
            }
          }
        }
      }
    }, null, 'handleDoubleClick');
  }, [zoomToEntity, safeOperation]);

  return {
    handleEnhancedMouseClick,
    handleDoubleClick
  };
};
  

  // Effect to update department visibility when threads change
  useEffect(() => {
    if (state.isInitialized) {
      updateDepartmentVisibility();
    }
  }, [state.visibleThreads, state.showOnlyConnectedEntities, state.isInitialized, updateDepartmentVisibility]);

 // Initialize tooltips with enhanced data - UPDATED
const initializeTooltips = useCallback(() => {
  safeOperation(() => {
    threadsData.forEach(thread => {
      const enhancedTooltip = `
        <strong>${thread.name}</strong><br>
        ${thread.description || 'Digital thread connecting multiple domains'}<br>
        <hr style="margin: 8px 0; border-color: rgba(255,255,255,0.2);">
        <div class="tooltip-metrics-grid">
          <div class="tooltip-metric">
            <span class="metric-label">Health:</span>
            <span class="metric-value ${thread.healthScore >= 80 ? 'excellent' : thread.healthScore >= 60 ? 'good' : 'poor'}">
              ${thread.healthScore}%
            </span>
          </div>
          <div class="tooltip-metric">
            <span class="metric-label">Connections:</span>
            <span class="metric-value">${thread.connections || 0}</span>
          </div>
          <div class="tooltip-metric">
            <span class="metric-label">Speed:</span>
            <span class="metric-value">${thread.speed || 1.0}x</span>
          </div>
          <div class="tooltip-metric">
            <span class="metric-label">Optimization:</span>
            <span class="metric-value">${thread.optimizationPotential || 60}%</span>
          </div>
        </div>
        ${thread.critical ? '<br>⚡ <em>Critical Thread</em>' : ''}
        ${thread.valueStream ? '<br>💎 <em>Value Stream</em>' : ''}
      `;
      
      tooltipManagerRef.current.registerTooltip(
        `thread-${thread.id}`,
        enhancedTooltip,
        thread,
        {
          showMetrics: true,
          enhancedStyling: true,
          persistent: false
        }
      );
    });
  }, null, 'initializeTooltips');
}, [threadsData, safeOperation]);

  // Create boundary spheres for visualization layers
  const createBoundarySpheres = useCallback((scene) => {
    safeOperation(() => {
      const boundaries = [
        { radius: 35, color: 0xff6b6b, name: 'Function Layer' },
        { radius: 65, color: 0x4ecdc4, name: 'Domain Layer' },
        { radius: 95, color: 0x45b7d1, name: 'System Layer' }
      ];

      boundaries.forEach(({ radius, color, name }) => {
        const geometry = new THREE.SphereGeometry(radius, 64, 64);
        const material = new THREE.MeshBasicMaterial({
          color: color,
          transparent: true,
          opacity: 0.03,
          wireframe: true,
          depthTest: false
        });

        const boundary = new THREE.Mesh(geometry, material);
        boundary.name = name;
        boundary.userData = { type: 'boundary', layer: name.toLowerCase() };
        scene.add(boundary);

        // Add subtle pulsing animation
        boundary.userData.pulseSpeed = 0.001;
        boundary.userData.pulsePhase = Math.random() * Math.PI * 2;
      });

    }, null, 'createBoundarySpheres');
  }, [safeOperation]);

  // Create enhanced department mesh with advanced features
  const createDepartmentMesh = useCallback((dept) => {
    return safeOperation(() => {
      const group = new THREE.Group();
      group.name = `department-${dept.id}`;
      group.position.set(...dept.position);

      // Main sphere with enhanced material
      const geometry = new THREE.SphereGeometry(2.5, 32, 32);
      const material = new THREE.MeshPhongMaterial({
        color: entityTypeColors[dept.type],
        emissive: entityTypeColors[dept.type],
        emissiveIntensity: 0.4,
        shininess: 100,
        transparent: true,
        opacity: 0.9,
        specular: 0x222222
      });

      const sphere = new THREE.Mesh(geometry, material);
      sphere.castShadow = true;
      sphere.receiveShadow = true;
      sphere.userData = { 
        type: 'department',
        id: dept.id,
        name: dept.name,
        departmentType: dept.type,
        layer: dept.layer,
        originalColor: entityTypeColors[dept.type],
        healthScore: dept.healthScore,
        connections: dept.connections,
        description: dept.description
      };
      group.add(sphere);

      // Enhanced glow effect with shader-based approach
      const glowGeometry = new THREE.SphereGeometry(3.0, 32, 32);
      const glowMaterial = new THREE.ShaderMaterial({
        uniforms: {
          glowColor: { value: new THREE.Color(entityTypeColors[dept.type]) },
          viewVector: { value: new THREE.Vector3() },
          intensity: { value: 1.5 }
        },
        vertexShader: `
          uniform vec3 viewVector;
          varying float intensity;
          void main() {
            vec3 vNormal = normalize(normalMatrix * normal);
            vec3 vNormel = normalize(normalMatrix * viewVector);
            intensity = pow(0.6 - dot(vNormal, vNormel), 2.0);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 glowColor;
          uniform float intensity;
          varying float intensityVarying;
          void main() {
            vec3 glow = glowColor * intensityVarying * intensity;
            gl_FragColor = vec4(glow, intensityVarying * 0.8);
          }
        `,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
        transparent: true
      });

      const glow = new THREE.Mesh(glowGeometry, glowMaterial);
      glow.userData = { isGlow: true };
      group.add(glow);

      // Enhanced floating label with better visibility
      const label = createFloatingLabel(dept.name, dept.type, dept.healthScore);
      label.position.set(0, 4, 0);
      label.userData = { isLabel: true };
      group.add(label);

      // Connection points for threads
      createConnectionPoints(group, dept);

      // Store reference to materials for later manipulation
      group.userData.materials = {
        sphere: material,
        glow: glowMaterial
      };

      // Store department data for quick access
      group.userData.departmentData = dept;

      return group;
    }, null, `createDepartmentMesh-${dept.id}`);
  }, [entityTypeColors, safeOperation]);

  // Create enhanced floating label
  const createFloatingLabel = useCallback((text, type, healthScore) => {
    return safeOperation(() => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 512;
      canvas.height = 128;
      
      // Clear canvas with transparent background
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      const textColor = entityTextColors[type];
      const healthColor = healthScore >= 80 ? '#00ff00' : healthScore >= 60 ? '#ffff00' : '#ff0000';
      
      // Enhanced text styling
      context.fillStyle = textColor;
      context.font = 'Bold 28px Arial, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      
      // Advanced text effects
      context.shadowColor = 'rgba(0, 0, 0, 0.8)';
      context.shadowBlur = 12;
      context.shadowOffsetX = 4;
      context.shadowOffsetY = 4;
      
      // Main text
      context.fillText(text, canvas.width / 2, canvas.height / 2 - 15);
      
      // Health indicator
      context.shadowBlur = 6;
      context.shadowOffsetX = 2;
      context.shadowOffsetY = 2;
      context.fillStyle = healthColor;
      context.font = 'Bold 18px Arial, sans-serif';
      context.fillText(`${healthScore}%`, canvas.width / 2, canvas.height / 2 + 20);

      // Reset shadow
      context.shadowColor = 'transparent';
      context.shadowBlur = 0;
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;

      const texture = new THREE.CanvasTexture(canvas);
      texture.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
      
      const material = new THREE.SpriteMaterial({ 
        map: texture,
        transparent: true,
        depthTest: false,
        opacity: 0.9
      });
      
      const sprite = new THREE.Sprite(material);
      sprite.scale.set(18, 6, 1);
      sprite.visible = state.showLabels;
      sprite.renderOrder = 999;
      
      sprite.userData = { 
        isLabel: true, 
        text: text,
        entityType: type,
        healthScore: healthScore
      };

      return sprite;
    }, new THREE.Group(), 'createFloatingLabel');
  }, [entityTextColors, state.showLabels, safeOperation]);

  // Create connection points for departments
  const createConnectionPoints = useCallback((group, dept) => {
    safeOperation(() => {
      const points = [];
      const pointCount = 8; // Points around the sphere
      
      for (let i = 0; i < pointCount; i++) {
        const phi = Math.acos(-1 + (2 * i) / pointCount);
        const theta = Math.sqrt(pointCount * Math.PI) * phi;
        
        const x = 2.8 * Math.cos(theta) * Math.sin(phi);
        const y = 2.8 * Math.sin(theta) * Math.sin(phi);
        const z = 2.8 * Math.cos(phi);
        
        const pointGeometry = new THREE.SphereGeometry(0.2, 8, 8);
        const pointMaterial = new THREE.MeshBasicMaterial({
          color: entityTypeColors[dept.type],
          transparent: true,
          opacity: 0.6
        });
        
        const point = new THREE.Mesh(pointGeometry, pointMaterial);
        point.position.set(x, y, z);
        point.visible = false; // Hidden by default
        point.userData = {
          type: 'connection-point',
          departmentId: dept.id,
          index: i
        };
        
        group.add(point);
        points.push(point);
      }
      
      group.userData.connectionPoints = points;
    }, null, 'createConnectionPoints');
  }, [entityTypeColors, safeOperation]);

  // Create departments visualization
 // FIXED: Create departments visualization with proper initialization
const createDepartmentsVisualization = useCallback((scene) => {
  safeOperation(() => {
    departmentsRef.current.clear();

    departmentsData.forEach(dept => {
      const department = createDepartmentMesh(dept);
      if (department) {
        // Set initial position and scale
        department.position.set(...dept.position);
        department.scale.set(1, 1, 1); // Start at normal scale
        department.visible = true; // Make visible by default
        
        scene.add(department);
        departmentsRef.current.set(dept.id, department);
        
        // Register tooltip for this department
        tooltipManagerRef.current.registerTooltip(
          dept.id, 
          `<strong>${dept.name}</strong><br>Type: ${dept.type}<br>Layer: ${dept.layer}<br>Connections: ${dept.connections}`,
          dept,
          {
            showMetrics: true,
            enhancedStyling: true,
            persistent: false
          }
        );
      }
    });

    console.log(`Created ${departmentsRef.current.size} departments`);
    
    // Update visibility after creation
    updateDepartmentVisibility();

  }, null, 'createDepartmentsVisualization');
}, [departmentsData, createDepartmentMesh, updateDepartmentVisibility, safeOperation]);

// Add these functions near your other thread management functions

// Toggle all threads visibility
const toggleAllThreadsVisibility = useCallback((makeVisible = true) => {
  safeSetState(prev => {
    const newVisibleThreads = makeVisible ? 
      new Set(threadsData.map(t => t.id)) : 
      new Set();
    
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.visible = makeVisible;
      }
    });
    
    updateDepartmentVisibility();
    createQuantumBloomEffect(makeVisible ? 1.2 : 0.8, 500);
    
    return { ...prev, visibleThreads: newVisibleThreads };
  });
}, [threadsData, updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);

// Show only selected threads
const showOnlySelectedThreads = useCallback(() => {
  safeSetState(prev => {
    const newVisibleThreads = new Set(prev.selectedThreads);
    
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.visible = prev.selectedThreads.has(threadId);
      }
    });
    
    updateDepartmentVisibility();
    createQuantumBloomEffect(1.5, 600);
    
    return { ...prev, visibleThreads: newVisibleThreads };
  });
}, [updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);

// Invert thread visibility
const invertThreadVisibility = useCallback(() => {
  safeSetState(prev => {
    const newVisibleThreads = new Set();
    
    threadsData.forEach(thread => {
      const wasVisible = prev.visibleThreads.has(thread.id);
      const threadObj = threadsRef.current.get(thread.id);
      
      if (threadObj?.group) {
        threadObj.group.visible = !wasVisible;
        if (!wasVisible) newVisibleThreads.add(thread.id);
      }
    });
    
    updateDepartmentVisibility();
    createQuantumBloomEffect(1.5, 600);
    
    return { ...prev, visibleThreads: newVisibleThreads };
  });
}, [threadsData, updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);

// Toggle individual thread visibility
const toggleSingleThreadVisibility = useCallback((threadId, event) => {
  event.stopPropagation();
  
  safeSetState(prev => {
    const newVisibleThreads = new Set(prev.visibleThreads);
    
    if (newVisibleThreads.has(threadId)) {
      newVisibleThreads.delete(threadId);
    } else {
      newVisibleThreads.add(threadId);
    }
    
    const threadObj = threadsRef.current.get(threadId);
    if (threadObj?.group) {
      threadObj.group.visible = newVisibleThreads.has(threadId);
    }
    
    updateDepartmentVisibility();
    createQuantumBloomEffect(0.5, 300);
    
    return { ...prev, visibleThreads: newVisibleThreads };
  });
}, [updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);

// Show threads by type
const showThreadsByType = useCallback((type) => {
  safeSetState(prev => {
    let newVisibleThreads = new Set();
    
    switch(type) {
      case 'golden':
        newVisibleThreads = new Set(prev.goldenThreads);
        break;
      case 'critical':
        const criticalThreads = threadsData.filter(t => t.critical).map(t => t.id);
        newVisibleThreads = new Set(criticalThreads);
        break;
      case 'value':
        const valueThreads = threadsData.filter(t => t.valueStream).map(t => t.id);
        newVisibleThreads = new Set(valueThreads);
        break;
      case 'healthy':
        const healthyThreads = threadsData.filter(t => (t.healthScore || 0) >= 80).map(t => t.id);
        newVisibleThreads = new Set(healthyThreads);
        break;
    }
    
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        threadObj.group.visible = newVisibleThreads.has(threadId);
      }
    });
    
    updateDepartmentVisibility();
    createQuantumBloomEffect(1.2, 500);
    
    return { ...prev, visibleThreads: newVisibleThreads };
  });
}, [threadsData, updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);
  // Create enhanced thread connection with advanced features
  const createThreadConnection = useCallback((startDept, endDept, color, speed, threadData) => {
    return safeOperation(() => {
      const startPos = startDept.position.clone();
      const endPos = endDept.position.clone();
      
      // Calculate dynamic curvature based on thread properties
      const curvature = threadData.critical ? 0.7 : 0.4;
      const complexity = (threadData.connections || 1) / 10;
      
      // Create curved path with intelligent control points
      const midPoint = new THREE.Vector3()
        .addVectors(startPos, endPos)
        .multiplyScalar(0.5);
      
      const direction = new THREE.Vector3()
        .subVectors(endPos, startPos)
        .normalize();
      
      const perpendicular = new THREE.Vector3()
        .crossVectors(direction, new THREE.Vector3(0, 1, 0))
        .normalize();
      
      const curveHeight = startPos.distanceTo(endPos) * curvature * (0.8 + Math.random() * 0.4);
      
      const controlPoint = midPoint.clone()
        .add(perpendicular.multiplyScalar(curveHeight))
        .add(new THREE.Vector3(
          (Math.random() - 0.5) * complexity * 10,
          (Math.random() - 0.5) * complexity * 10,
          (Math.random() - 0.5) * complexity * 10
        ));

      const curve = new THREE.QuadraticBezierCurve3(startPos, controlPoint, endPos);
      const points = curve.getPoints(100); // Higher resolution for smoother curves
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      
      // Enhanced material with dynamic properties
      const material = new THREE.LineBasicMaterial({
        color: color,
        transparent: true,
        opacity: threadData.critical ? 0.8 : 0.6,
        linewidth: threadData.critical ? 3 : 2
      });

      const line = new THREE.Line(geometry, material);
      
      line.userData = {
        type: 'thread-connection',
        startDept: startDept.userData,
        endDept: endDept.userData,
        curve: curve,
        speed: speed,
        threadData: threadData,
        critical: threadData.critical,
        valueStream: threadData.valueStream,
        originalColor: color
      };

      return { line, curve, controlPoint };
    }, { line: new THREE.Group(), curve: null }, 'createThreadConnection');
  }, [safeOperation]);

  // Create enhanced data particles with advanced behavior
  const createDataParticle = useCallback((curve, color, speed, threadData) => {
    return safeOperation(() => {
      const size = threadData.critical ? 0.4 : 0.3;
      const geometry = new THREE.SphereGeometry(size, 12, 12);
      
      // Enhanced material with emissive properties
      const material = new THREE.MeshBasicMaterial({
        color: color,
        emissive: color,
        emissiveIntensity: 0.3,
        transparent: true,
        opacity: 0.9
      });

      const particle = new THREE.Mesh(geometry, material);
      
      const initialProgress = Math.random();
      const particleSpeed = speed * (0.7 + Math.random() * 0.6);
      const pulseSpeed = 2 + Math.random() * 3;
      
      particle.userData = {
        type: 'data-particle',
        curve: curve,
        progress: initialProgress,
        speed: particleSpeed,
        pulseSpeed: pulseSpeed,
        color: color,
        originalColor: color.clone(),
        threadData: threadData,
        size: size
      };

      return particle;
    }, new THREE.Group(), 'createDataParticle');
  }, [safeOperation]);

  // Create individual thread visualization with enhanced features
  const createThreadVisualization = useCallback((scene, thread) => {
    return safeOperation(() => {
      const threadGroup = new THREE.Group();
      threadGroup.name = `thread-${thread.id}`;
      
      const isGolden = state.goldenThreads.has(thread.id);
      const threadColor = isGolden ? new THREE.Color(entityTypeColors.golden) : new THREE.Color(thread.color);
      
      // Get connected departments for this thread
      const connectedDepartments = departmentsData.filter(dept => 
        (thread.connects && thread.connects.includes(dept.name)) || 
        (thread.domains && thread.domains.includes(dept.name)) ||
        (thread.systems && thread.systems.includes(dept.name))
      );

      if (connectedDepartments.length < 2) {
        console.warn(`Thread ${thread.id} has insufficient connections`);
        return null;
      }

      // Create connections between departments in sequence
      const connections = [];
      
      for (let i = 0; i < connectedDepartments.length - 1; i++) {
        const startDept = departmentsRef.current.get(connectedDepartments[i].id);
        const endDept = departmentsRef.current.get(connectedDepartments[i + 1].id);
        
        if (startDept && endDept) {
          const connection = createThreadConnection(startDept, endDept, threadColor, thread.speed, thread);
          
          if (connection.line) {
            threadGroup.add(connection.line);
            connections.push(connection);
            
            // Add enhanced data particles
            const particleCount = thread.critical ? 10 : 6;
            for (let j = 0; j < particleCount; j++) {
              const particle = createDataParticle(connection.curve, threadColor, thread.speed, thread);
              if (particle) {
                threadGroup.add(particle);
                particlesRef.current.push(particle);
              }
            }
            
            // Add sequence indicator for complex threads
            if (connectedDepartments.length > 2) {
              const sequenceLabel = createSequenceLabel(i + 1, connection.controlPoint);
              if (sequenceLabel) {
                threadGroup.add(sequenceLabel);
              }
            }
          }
        }
      }

      // Only add to scene if we have valid connections
      if (threadGroup.children.length > 0) {
        scene.add(threadGroup);
        
        threadsRef.current.set(thread.id, {
          group: threadGroup,
          thread: thread,
          connections: connections,
          isGolden: isGolden,
          isVisible: true
        });

        return threadGroup;
      }
      
      return null;
    }, null, `createThreadVisualization-${thread.id}`);
  }, [departmentsData, state.goldenThreads, entityTypeColors, createThreadConnection, createDataParticle, safeOperation]);

  // Create sequence label for multi-hop threads
// ENHANCED SEQUENCE LABEL WITH PERFECT POSITIONING AND HARMONIOUS BLINKING
const createSequenceLabel = useCallback((sequenceNumber, controlPoint, threadColor, threadId) => {
  return safeOperation(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 80; // Increased size for better visibility
    canvas.height = 80;
    
    // Convert thread color to RGB for the label
    const color = new THREE.Color(threadColor);
    const rgbColor = `rgb(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)})`;
    
    // Create glowing background with thread color
    const gradient = context.createRadialGradient(40, 40, 0, 40, 40, 35);
    gradient.addColorStop(0, `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, 0.9)`);
    gradient.addColorStop(0.7, `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, 0.4)`);
    gradient.addColorStop(1, `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, 0.1)`);
    
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(40, 40, 35, 0, 2 * Math.PI);
    context.fill();
    
    // Add outer glow
    context.strokeStyle = rgbColor;
    context.lineWidth = 3;
    context.shadowColor = rgbColor;
    context.shadowBlur = 15;
    context.stroke();
    
    // Add sequence number with high contrast
    context.fillStyle = '#ffffff';
    context.font = 'Bold 32px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.shadowColor = 'rgba(0, 0, 0, 0.8)';
    context.shadowBlur = 8;
    context.shadowOffsetX = 2;
    context.shadowOffsetY = 2;
    context.fillText(sequenceNumber.toString(), 40, 40);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true,
      depthTest: false,
      opacity: 0.9
    });
    
    const sprite = new THREE.Sprite(material);
    
    // Calculate optimal position along the thread curve
    const optimalPosition = calculateOptimalLabelPosition(controlPoint, sequenceNumber);
    sprite.position.copy(optimalPosition);
    
    sprite.scale.set(4, 4, 1); // Larger scale for better visibility
    sprite.renderOrder = 1000;
    sprite.visible = state.showLabels;
    
    // Store animation properties
    sprite.userData = {
      type: 'sequence-label',
      sequenceNumber: sequenceNumber,
      threadId: threadId,
      threadColor: threadColor,
      baseScale: 4,
      blinkPhase: (sequenceNumber - 1) * 0.2, // Staggered blinking
      pulseSpeed: 0.02,
      originalPosition: optimalPosition.clone()
    };

    return sprite;
  }, new THREE.Group(), 'createSequenceLabel');
}, [state.showLabels, safeOperation]);

// CALCULATE OPTIMAL LABEL POSITION TO TOUCH THE THREAD
const calculateOptimalLabelPosition = useCallback((controlPoint, sequenceNumber) => {
  // Position the label slightly above the control point to touch the thread
  const position = controlPoint.clone();
  
  // Add some vertical offset to ensure it touches the thread curve
  position.y += 2;
  
  // Add slight variation based on sequence number for better spacing
  const angleVariation = (sequenceNumber % 3) * 0.3;
  position.x += Math.cos(angleVariation) * 1.5;
  position.z += Math.sin(angleVariation) * 1.5;
  
  return position;
}, []);

  // Create digital threads visualization
  const createDigitalThreadsVisualization = useCallback((scene) => {
    safeOperation(() => {
      threadsRef.current.clear();
      particlesRef.current = [];

      let createdCount = 0;
      threadsData.forEach(thread => {
        const threadViz = createThreadVisualization(scene, thread);
        if (threadViz) {
          createdCount++;
        }
      });

      console.log(`Created ${createdCount} of ${threadsData.length} thread visualizations`);

      safeSetState(prev => ({
        ...prev,
        visibleThreads: new Set(threadsData.map(t => t.id))
      }));

    }, null, 'createDigitalThreadsVisualization');
  }, [threadsData, createThreadVisualization, safeSetState, safeOperation]);


  // Enhanced entrance animation with multiple stages
  const startQuantumEntranceAnimation = useCallback(() => {
    let progress = 0;
    const animationStages = {
      camera: 0,
      departments: 0,
      threads: 0,
      effects: 0
    };
    
    const animateEntrance = () => {
      try {
        if (progress < 1) {
          progress = Math.min(progress + 0.012, 1);
          
          // Stage 1: Camera animation (0-40%)
          if (progress <= 0.4) {
            animationStages.camera = progress / 0.4;
            if (cameraRef.current) {
              const cameraProgress = 1 - Math.pow(1 - animationStages.camera, 3);
              const orbitProgress = cameraProgress * Math.PI * 2;
              
              cameraRef.current.position.set(
                100 * Math.cos(orbitProgress) * cameraProgress,
                80 * cameraProgress, 
                100 * Math.sin(orbitProgress) * cameraProgress
              );
            }
          }
          
          // Stage 2: Department animation (20-70%)
          if (progress >= 0.2 && progress <= 0.7) {
            animationStages.departments = (progress - 0.2) / 0.5;
            departmentsRef.current.forEach((dept, index) => {
              const delay = (index / Math.max(departmentsRef.current.size, 1)) * 0.3;
              const deptProgress = Math.max(0, (animationStages.departments - delay) / (1 - delay));
              const quantumScale = deptProgress + 0.3 * Math.sin(deptProgress * Math.PI * 3);
              dept.scale.setScalar(quantumScale);
              
              // Pulsing opacity and emissive intensity
              if (dept.userData.materials) {
                dept.userData.materials.sphere.opacity = 0.9 * deptProgress;
                dept.userData.materials.sphere.emissiveIntensity = 0.3 + 0.2 * Math.sin(deptProgress * Math.PI * 4);
              }
            });
          }
          
          // Stage 3: Thread animation (50-90%)
          if (progress >= 0.5 && progress <= 0.9) {
            animationStages.threads = (progress - 0.5) / 0.4;
            threadsRef.current.forEach((threadObj, index) => {
              const delay = (index / Math.max(threadsRef.current.size, 1)) * 0.2;
              const threadProgress = Math.max(0, (animationStages.threads - delay) / (1 - delay));
              
              if (threadObj.group) {
                threadObj.group.children.forEach(child => {
                  if (child.userData?.type === 'thread-connection' && child.material) {
                    child.material.opacity = 0.6 * threadProgress;
                  }
                });
              }
            });
          }
          
          // Stage 4: Effects (80-100%)
          if (progress >= 0.8) {
            animationStages.effects = (progress - 0.8) / 0.2;
            // Additional effects can be added here
          }
          
          animationRef.current = requestAnimationFrame(animateEntrance);
        } else {
          // Entrance complete - trigger quantum wow moment
          createQuantumWowMoment();
          safeSetState(prev => ({ 
            ...prev, 
            animationPhase: 'quantum-active',
            isPlaying: true
          }));
          
          console.log('Quantum visualization fully initialized');
        }
      } catch (error) {
        console.warn('Quantum entrance animation warning:', error);
      }
    };
    
    animateEntrance();
  }, [createQuantumWowMoment, safeSetState]);

  // Initialize Three.js with enhanced features and fallbacks
// Enhanced Quantum Effects and Visual Enhancements


// Initialize the camera hook
//const { zoomToEntity, cameraState } = useQuantumCamera();


// Create brilliance particle for golden threads
const createBrillianceParticle = useCallback((color) => {
  return safeOperation(() => {
    const geometry = new THREE.OctahedronGeometry(0.4, 0);
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });
    
    const particle = new THREE.Mesh(geometry, material);
    return particle;
  }, new THREE.Group(), 'createBrillianceParticle');
}, [safeOperation]);

// Highlight random connection for visual interest
const highlightRandomConnection = useCallback(() => {
  return safeOperation(() => {
    const visibleThreads = Array.from(threadsRef.current.entries())
      .filter(([id, thread]) => thread.group && thread.group.visible);
    
    if (visibleThreads.length === 0) return;
    
    const randomIndex = Math.floor(Math.random() * visibleThreads.length);
    const [threadId, threadObj] = visibleThreads[randomIndex];
    
    // Highlight the thread
    threadObj.group.children.forEach(child => {
      if (child.userData?.type === 'thread-connection' && child.material) {
        const originalColor = child.material.color.clone();
        const highlightColor = new THREE.Color(0xffffff);
        
        child.material.color = highlightColor;
        child.material.opacity = 1.0;
        
        // Restore after delay
        setTimeout(() => {
          if (child.material) {
            child.material.color = originalColor;
            child.material.opacity = 0.6;
          }
        }, 1000);
      }
    });
    
    // Create a subtle effect
    createQuantumWowMoment(threadId);
    
  }, null, 'highlightRandomConnection');
}, [createQuantumWowMoment, safeOperation]);

// Enhanced thread connection with advanced visual features
const createEnhancedThreadConnection = useCallback((startDept, endDept, color, speed, threadData) => {
  return safeOperation(() => {
    const startPos = startDept.position.clone();
    const endPos = endDept.position.clone();
    
    // Calculate dynamic curvature based on thread properties
    const isCritical = threadData.critical || false;
    const isGolden = state.goldenThreads.has(threadData.id);
    const complexity = Math.min(1, (threadData.connections || 1) / 10);
    
    // Enhanced curvature for important threads
    const curvature = isCritical ? 0.8 : isGolden ? 0.6 : 0.4;
    const curveHeight = startPos.distanceTo(endPos) * curvature * (0.8 + Math.random() * 0.4);
    
    // Create control point with intelligent placement
    const midPoint = new THREE.Vector3()
      .addVectors(startPos, endPos)
      .multiplyScalar(0.5);
    
    const direction = new THREE.Vector3()
      .subVectors(endPos, startPos)
      .normalize();
    
    const perpendicular = new THREE.Vector3()
      .crossVectors(direction, new THREE.Vector3(0, 1, 0))
      .normalize();
    
    const controlPoint = midPoint.clone()
      .add(perpendicular.multiplyScalar(curveHeight))
      .add(new THREE.Vector3(
        (Math.random() - 0.5) * complexity * 15,
        (Math.random() - 0.5) * complexity * 15,
        (Math.random() - 0.5) * complexity * 15
      ));

    const curve = new THREE.QuadraticBezierCurve3(startPos, controlPoint, endPos);
    const points = curve.getPoints(80); // Higher resolution for smoother curves
    
    // Enhanced geometry with better line quality
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    
    // Advanced material with dynamic properties
    const material = new THREE.LineBasicMaterial({
      color: color,
      transparent: true,
      opacity: isCritical ? 0.8 : isGolden ? 0.7 : 0.6,
      linewidth: isCritical ? 3 : isGolden ? 2.5 : 2
    });

    const line = new THREE.Line(geometry, material);
    
    // Enhanced user data with thread information
    line.userData = {
      type: 'thread-connection',
      startDept: startDept.userData,
      endDept: endDept.userData,
      curve: curve,
      speed: speed,
      threadData: threadData,
      critical: isCritical,
      golden: isGolden,
      valueStream: threadData.valueStream || false,
      originalColor: color,
      healthScore: threadData.healthScore || 50
    };

    return { line, curve, controlPoint };
    
  }, { line: new THREE.Group(), curve: null }, 'createEnhancedThreadConnection');
}, [state.goldenThreads, safeOperation]);

// Enhanced data particle with advanced behavior
const createEnhancedDataParticle = useCallback((curve, color, speed, threadData) => {
  return safeOperation(() => {
    const isCritical = threadData.critical || false;
    const isGolden = state.goldenThreads.has(threadData.id);
    
    // Dynamic particle properties based on thread importance
    const size = isCritical ? 0.5 : isGolden ? 0.4 : 0.3;
    const segments = isCritical ? 10 : isGolden ? 8 : 6;
    
    const geometry = new THREE.SphereGeometry(size, segments, segments);
    
    // Enhanced material with emissive properties for important threads
    const material = new THREE.MeshBasicMaterial({
      color: color,
      emissive: isCritical || isGolden ? color : 0x000000,
      emissiveIntensity: isCritical ? 0.5 : isGolden ? 0.3 : 0,
      transparent: true,
      opacity: 0.9
    });

    const particle = new THREE.Mesh(geometry, material);
    
    const initialProgress = Math.random();
    const particleSpeed = speed * (0.6 + Math.random() * 0.8);
    const pulseSpeed = isCritical ? 4 : isGolden ? 3 : 2 + Math.random() * 2;
    const pulseIntensity = isCritical ? 0.4 : isGolden ? 0.3 : 0.2;
    
    particle.userData = {
      type: 'data-particle',
      curve: curve,
      progress: initialProgress,
      speed: particleSpeed,
      pulseSpeed: pulseSpeed,
      pulseIntensity: pulseIntensity,
      color: color,
      originalColor: color.clone(),
      threadData: threadData,
      size: size,
      isCritical: isCritical,
      isGolden: isGolden
    };

    return particle;
    
  }, new THREE.Group(), 'createEnhancedDataParticle');
}, [state.goldenThreads, safeOperation]);

// Enhanced thread visualization using the new connection and particle functions
// ENHANCED THREAD VISUALIZATION WITH SEQUENCE LABELS
const createEnhancedThreadVisualization = useCallback((scene, thread) => {
  return safeOperation(() => {
    const threadGroup = new THREE.Group();
    threadGroup.name = `thread-${thread.id}`;
    
    const isGolden = state.goldenThreads.has(thread.id);
    const threadColor = isGolden ? new THREE.Color(entityTypeColors.golden) : new THREE.Color(thread.color);
    
    // Get connected departments for this thread
    const connectedDepartments = departmentsData.filter(dept => 
      (thread.connects && thread.connects.includes(dept.name)) || 
      (thread.domains && thread.domains.includes(dept.name)) ||
      (thread.systems && thread.systems.includes(dept.name))
    );

    if (connectedDepartments.length < 2) {
      console.warn(`Thread ${thread.id} has insufficient connections`);
      return null;
    }

    // Create enhanced connections between departments
    const connections = [];
    
    for (let i = 0; i < connectedDepartments.length - 1; i++) {
      const startDept = departmentsRef.current.get(connectedDepartments[i].id);
      const endDept = departmentsRef.current.get(connectedDepartments[i + 1].id);
      
      if (startDept && endDept) {
        const connection = createEnhancedThreadConnection(startDept, endDept, threadColor, thread.speed, thread);
        
        if (connection.line) {
          threadGroup.add(connection.line);
          connections.push(connection);
          
          // Add enhanced data particles
          const particleCount = thread.critical ? 12 : thread.valueStream ? 8 : 6;
          for (let j = 0; j < particleCount; j++) {
            const particle = createEnhancedDataParticle(connection.curve, threadColor, thread.speed, thread);
            if (particle) {
              threadGroup.add(particle);
              particlesRef.current.push(particle);
            }
          }
          
          // ENHANCED: Add sequence indicator for complex threads
          if (connectedDepartments.length > 2) {
            const sequenceLabel = createSequenceLabel(i + 1, connection.controlPoint, threadColor, thread.id);
            if (sequenceLabel) {
              threadGroup.add(sequenceLabel);
              
              // Position label to touch the thread
              positionLabelOnThread(sequenceLabel, connection.curve, i + 1, connectedDepartments.length);
            }
          }
        }
      }
    }

    // Only add to scene if we have valid connections
    if (threadGroup.children.length > 0) {
      scene.add(threadGroup);
      
      threadsRef.current.set(thread.id, {
        group: threadGroup,
        thread: thread,
        connections: connections,
        isGolden: isGolden,
        isVisible: true,
        sequenceLabels: threadGroup.children.filter(child => 
          child.userData?.type === 'sequence-label'
        )
      });

      return threadGroup;
    }
    
    return null;
    
  }, null, `createEnhancedThreadVisualization-${thread.id}`);
}, [departmentsData, state.goldenThreads, entityTypeColors, createEnhancedThreadConnection, createEnhancedDataParticle, createSequenceLabel, safeOperation]);


// POSITION LABEL TO TOUCH THE THREAD
const positionLabelOnThread = useCallback((label, curve, sequenceNumber, totalSegments) => {
  // Calculate optimal position along the curve to ensure it touches the thread
  const t = sequenceNumber / (totalSegments + 1); // Position along the curve (0 to 1)
  const threadPosition = curve.getPointAt(t);
  
  // Get tangent to position label perpendicular to thread
  const tangent = curve.getTangentAt(t);
  const perpendicular = new THREE.Vector3(-tangent.z, tangent.y, tangent.x).normalize();
  
  // Position label slightly above the thread
  const labelPosition = threadPosition.clone();
  labelPosition.add(perpendicular.multiplyScalar(1.5));
  labelPosition.y += 1; // Additional vertical offset
  
  label.position.copy(labelPosition);
  label.userData.originalPosition = labelPosition.clone();
}, []);


// Enhanced initialization with quantum effects
const initializeWithQuantumEffects = useCallback(() => {
  safeOperation(() => {
    // Initialize quantum bloom effect
    createQuantumBloomEffect(1.5, 500);
    
    
    // Set up periodic random highlights
    setInterval(() => {
      if (state.isPlaying && Math.random() < 0.3) { // 30% chance every interval
        highlightRandomConnection();
      }
    }, 8000); // Every 8 seconds
    
    
  }, null, 'initializeWithQuantumEffects');
}, [createQuantumBloomEffect, highlightRandomConnection, state.isPlaying, state.goldenThreads, safeOperation]);


const createEnhancedDigitalThreadsVisualization = useCallback((scene) => {
  safeOperation(() => {
    threadsRef.current.clear();
    particlesRef.current = [];

    let createdCount = 0;
    threadsData.forEach(thread => {
      const threadViz = createEnhancedThreadVisualization(scene, thread);
      if (threadViz) {
        createdCount++;
      }
    });

    console.log(`Created ${createdCount} of ${threadsData.length} enhanced thread visualizations`);

    safeSetState(prev => ({
      ...prev,
      visibleThreads: new Set(threadsData.map(t => t.id))
    }));

  }, null, 'createEnhancedDigitalThreadsVisualization');
}, [threadsData, createEnhancedThreadVisualization, safeSetState, safeOperation]);


 // Enhanced initialization with department visibility updates
  const initializeQuantumVisualization = useCallback(async () => {
    try {
      safeSetState(prev => ({ 
        ...prev, 
        isLoading: true, 
        loadingProgress: 0 
      }));

      // Simple step-by-step initialization
      const steps = [
        { action: () => initializeThreeJS(), progress: 20 },
        { action: () => initializeTooltips(), progress: 30 },
        { action: () => createBoundarySpheres(sceneRef.current), progress: 50 },
        { action: () => createDepartmentsVisualization(sceneRef.current), progress: 65 },
        { action: () => createEnhancedDigitalThreadsVisualization(sceneRef.current), progress: 80 },
      ];

      for (const step of steps) {
        await step.action();
        safeSetState(prev => ({ ...prev, loadingProgress: step.progress }));
      }

      // Complete initialization
      safeSetState(prev => ({ 
        ...prev, 
        loadingProgress: 100, 
        isLoading: false, 
        isInitialized: true,
        isPlaying: true 
      }));

      console.log('✅ Quantum visualization ready!');

    } catch (error) {
      console.error('Initialization failed:', error);
      safeSetState({ 
        hasError: true, 
        errorMessage: error.message,
        isLoading: false 
      });
    }
  }, [safeSetState]);




// ENHANCED ZOOM FUNCTIONS

  
// Enhanced OrbitControls initialization with proper zoom and pan
// HIGH-PERFORMANCE CAMERA & ZOOM SYSTEM
const initializeThreeJS = useCallback(() => {
  return new Promise((resolve, reject) => {
    safeOperation(() => {
      if (!containerRef.current) {
        reject(new Error('Visualization container not available'));
        return;
      }

      const width = containerRef.current.clientWidth || 800;
      const height = containerRef.current.clientHeight || 600;

      console.log('Initializing High-Performance Three.js with dimensions:', width, 'x', height);

      // Scene with optimized environment
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x050518);
      scene.fog = new THREE.FogExp2(0x050518, 0.001); // Lighter fog for better zoom
      sceneRef.current = scene;

      // HIGH-PERFORMANCE CAMERA WITH 10X ZOOM CAPABILITY
      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000); // Extended far plane
      camera.position.set(120, 80, 120);
      camera.lookAt(0, 0, 0);
      cameraRef.current = camera;

      // OPTIMIZED WEBGL RENDERER
      let renderer;
      try {
        const canvas = document.createElement('canvas');
        const contextAttributes = {
          alpha: true,
          antialias: true,
          powerPreference: "high-performance",
          preserveDrawingBuffer: false // Better performance
        };

        renderer = new THREE.WebGLRenderer(contextAttributes);
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Limit pixel ratio for performance
        renderer.shadowMap.enabled = false; // Disable shadows for zoom performance
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.autoClear = true;
        rendererRef.current = renderer;

        // Clear and append to container
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }
        containerRef.current.appendChild(renderer.domElement);
        renderer.domElement.style.touchAction = 'none';

      } catch (webglError) {
        reject(new Error('WebGL initialization failed'));
        return;
      }

      // ULTRA-PERFORMANCE ORBITCONTROLS WITH 10X ZOOM
      try {
        const controls = new OrbitControls(camera, renderer.domElement);
        
        // Enhanced control settings for 10x zoom
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.enableZoom = true;
        controls.zoomSpeed = 3.0; // Faster zoom
        controls.enablePan = true;
        controls.panSpeed = 2.0;
        controls.enableRotate = true;
        controls.rotateSpeed = 1.0;
        
        // EXTENDED ZOOM LIMITS FOR 10X CAPABILITY
        controls.minDistance = 2;    // Can zoom in extremely close (10x from default)
        controls.maxDistance = 2000; // Can zoom out very far
        
        // Enhanced rotation limits
        controls.minPolarAngle = 0;
        controls.maxPolarAngle = Math.PI;
        
        // Ultra-smooth zoom
        controls.smoothZoom = true;
        controls.zoomDampingFactor = 0.05;
        
        // Mouse/touch configuration
        controls.mouseButtons = {
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.PAN
        };
        
        controls.touches = {
          ONE: THREE.TOUCH.ROTATE,
          TWO: THREE.TOUCH.DOLLY_PAN
        };

        // Enhanced zoom event listeners with performance optimization
        controls.addEventListener('start', () => {
          //safeSetState(prev => ({ ...prev, isPlaying: false }));
          // Reduce quality during interaction for performance
          //optimizeVisualization('performance');
        });

        controls.addEventListener('end', () => {
          setTimeout(() => {
            //safeSetState(prev => ({ ...prev, isPlaying: true }));
            // Restore quality after interaction
            //optimizeVisualization(performanceMode);
          }, 1000);
        });

        controls.addEventListener('change', () => {
          // Update zoom level state with 10x scale
          if (cameraRef.current) {
            const distance = cameraRef.current.position.distanceTo(controls.target);
            const zoomLevel = Math.max(0.1, Math.min(10, 120 / distance)); // 10x max zoom
            safeSetState(prev => ({ ...prev, zoomLevel }));
            
            // Dynamic performance optimization based on zoom
            updateZoomPerformance(zoomLevel);
            
            // Update text and LOD based on zoom
            updateTextScaling(zoomLevel);
            updateLODBasedOnZoom(zoomLevel);
          }
        });

        controlsRef.current = controls;
        console.log('Ultra-performance OrbitControls with 10x zoom initialized');

      } catch (controlsError) {
        console.warn('OrbitControls initialization warning:', controlsError);
      }

      // OPTIMIZED LIGHTING FOR ZOOM PERFORMANCE
      const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(100, 100, 50);
      directionalLight.castShadow = false; // Disable shadows for performance
      scene.add(directionalLight);

      // Minimal accent lights
      const pointLight1 = new THREE.PointLight(0x00ff88, 0.3, 300);
      pointLight1.position.set(50, 50, 50);
      scene.add(pointLight1);

      resolve();
    }, reject, 'initializeThreeJS');
  });
}, [safeSetState, performanceMode, optimizeVisualization, safeOperation]);

// ZOOM-BASED PERFORMANCE OPTIMIZATION SYSTEM
const updateZoomPerformance = useCallback((zoomLevel) => {
  safeOperation(() => {
    // Dynamic performance settings based on zoom level
    if (zoomLevel > 5) {
      // Maximum zoom - highest quality
      setPerformanceMode('high');
      updateThreadLOD('high');
      safeSetState(prev => ({ 
        ...prev, 
        showParticles: true,
        showLabels: true 
      }));
    } else if (zoomLevel > 2) {
      // Medium zoom - balanced quality
      setPerformanceMode('balanced');
      updateThreadLOD('medium');
    } else {
      // Far zoom - performance mode
      setPerformanceMode('performance');
      updateThreadLOD('low');
      safeSetState(prev => ({ 
        ...prev, 
        showParticles: zoomLevel > 0.5 // Hide particles at very far zoom
      }));
    }
    
    optimizeVisualization(performanceMode);
    
  }, null, 'updateZoomPerformance');
}, [setPerformanceMode, updateThreadLOD, optimizeVisualization, safeSetState, safeOperation]);

// ZOOM-BASED LOD SYSTEM
const updateLODBasedOnZoom = useCallback((zoomLevel) => {
  safeOperation(() => {
    let lodLevel = 'medium';
    
    if (zoomLevel > 5) {
      lodLevel = 'high'; // Maximum detail at close zoom
    } else if (zoomLevel > 2) {
      lodLevel = 'medium'; // Balanced detail
    } else {
      lodLevel = 'low'; // Reduced detail at far zoom
    }
    
    if (lodLevel !== lodLevel) {
      updateThreadLOD(lodLevel);
    }
    
    // Dynamic particle visibility
    const particleVisibility = zoomLevel > 0.8;
    particlesRef.current.forEach(particle => {
      particle.visible = particleVisibility;
    });
    
  }, null, 'updateLODBasedOnZoom');
}, [updateThreadLOD, safeOperation]);

// ADVANCED ZOOM FUNCTIONS WITH 10X CAPABILITY
const zoomTo10x = useCallback((targetPosition = new THREE.Vector3(0, 0, 0)) => {
  safeOperation(() => {
    if (!cameraRef.current || !controlsRef.current) return;
    
    const zoomDistance = 12; // 10x zoom distance
    animateCameraToTarget(targetPosition, zoomDistance, 600);
    
    // Set to maximum zoom level
    safeSetState(prev => ({ ...prev, zoomLevel: 10 }));
    updateZoomPerformance(10);
    updateTextScaling(10);
    
    createQuantumBloomEffect(3.0, 800);
    
    console.log('Zoomed to 10x maximum level');
    
  }, null, 'zoomTo10x');
}, [animateCameraToTarget, createQuantumBloomEffect, updateZoomPerformance, updateTextScaling, safeOperation, safeSetState]);

const zoomTo5x = useCallback((targetPosition = new THREE.Vector3(0, 0, 0)) => {
  safeOperation(() => {
    if (!cameraRef.current || !controlsRef.current) return;
    
    const zoomDistance = 24; // 5x zoom distance
    animateCameraToTarget(targetPosition, zoomDistance, 500);
    
    safeSetState(prev => ({ ...prev, zoomLevel: 5 }));
    updateZoomPerformance(5);
    updateTextScaling(5);
    
    createQuantumBloomEffect(2.0, 600);
    
  }, null, 'zoomTo5x');
}, [animateCameraToTarget, createQuantumBloomEffect, updateZoomPerformance, updateTextScaling, safeOperation, safeSetState]);

const zoomToOverview = useCallback(() => {
  safeOperation(() => {
    if (!cameraRef.current || !controlsRef.current) return;
    
    const zoomDistance = 400; // Overview distance
    animateCameraToTarget(new THREE.Vector3(0, 0, 0), zoomDistance, 800);
    
    safeSetState(prev => ({ ...prev, zoomLevel: 0.3 }));
    updateZoomPerformance(0.3);
    updateTextScaling(0.3);
    
    createQuantumBloomEffect(1.2, 500);
    
  }, null, 'zoomToOverview');
}, [animateCameraToTarget, createQuantumBloomEffect, updateZoomPerformance, updateTextScaling, safeOperation, safeSetState]);

const zoomToEntityUltra = useCallback((entityId, entityType, zoomMultiplier = 5) => {
  safeOperation(() => {
    let targetPosition = new THREE.Vector3(0, 0, 0);
    let zoomDistance = 120 / zoomMultiplier; // Dynamic zoom based on multiplier

    if (entityType === 'department') {
      const dept = departmentsRef.current.get(entityId);
      if (dept) {
        targetPosition.copy(dept.position);
        zoomDistance = 60 / zoomMultiplier;
      }
    } else if (entityType === 'thread') {
      const threadObj = threadsRef.current.get(entityId);
      if (threadObj?.group) {
        const bbox = new THREE.Box3().setFromObject(threadObj.group);
        targetPosition = bbox.getCenter(new THREE.Vector3());
        zoomDistance = 80 / zoomMultiplier;
      }
    }

    animateCameraToTarget(targetPosition, zoomDistance, 600);
    
    safeSetState(prev => ({ ...prev, zoomLevel: zoomMultiplier }));
    updateZoomPerformance(zoomMultiplier);
    updateTextScaling(zoomMultiplier);
    
    createQuantumBloomEffect(2.0, 600);

  }, null, 'zoomToEntityUltra');
}, [animateCameraToTarget, createQuantumBloomEffect, updateZoomPerformance, updateTextScaling, safeOperation, safeSetState]);


// EPIC Entrance Animation with Continuous Wow Moments

// Enhanced boundary spheres with visual appeal
const createEnhancedBoundarySpheres = useCallback((scene) => {
  safeOperation(() => {
    const boundaries = [
      { radius: 40, color: 0xff6b6b, name: 'Function Realm' },
      { radius: 75, color: 0x4ecdc4, name: 'Domain Nexus' },
      { radius: 110, color: 0x45b7d1, name: 'System Cosmos' }
    ];

    boundaries.forEach(({ radius, color, name }, index) => {
      const geometry = new THREE.SphereGeometry(radius, 48, 48);
      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.08,
        wireframe: true,
        depthTest: false
      });

      const boundary = new THREE.Mesh(geometry, material);
      boundary.name = name;
      boundary.userData = { 
        type: 'boundary', 
        layer: name.toLowerCase(),
        pulseSpeed: 0.002 + index * 0.001,
        pulsePhase: Math.random() * Math.PI * 2
      };
      scene.add(boundary);
    });
  });
}, [safeOperation]);

// Enhanced departments with better visual hierarchy
const createEnhancedDepartmentsVisualization = useCallback((scene) => {
  safeOperation(() => {
    departmentsRef.current.clear();

    departmentsData.forEach(dept => {
      const department = createEnhancedDepartmentMesh(dept);
      if (department) {
        department.scale.set(0, 0, 0); // Start invisible for animation
        scene.add(department);
        departmentsRef.current.set(dept.id, department);
      }
    });
  });
}, [departmentsData, safeOperation]);

// Enhanced department mesh with better visual design
const createEnhancedDepartmentMesh = useCallback((dept) => {
  return safeOperation(() => {
    const group = new THREE.Group();
    group.name = `department-${dept.id}`;
    group.position.set(...dept.position);

    // Main sphere with enhanced material
    const geometry = new THREE.SphereGeometry(3.0, 24, 24);
    const material = new THREE.MeshPhongMaterial({
      color: entityTypeColors[dept.type],
      emissive: entityTypeColors[dept.type],
      emissiveIntensity: 0.5,
      shininess: 80,
      transparent: true,
      opacity: 0.9,
      specular: 0x333333
    });

    const sphere = new THREE.Mesh(geometry, material);
    sphere.castShadow = true;
    sphere.userData = { 
      type: 'department',
      id: dept.id,
      name: dept.name,
      departmentType: dept.type,
      layer: dept.layer,
      originalColor: entityTypeColors[dept.type],
      healthScore: dept.healthScore
    };
    group.add(sphere);

    // Enhanced glow effect
    const glowGeometry = new THREE.SphereGeometry(3.8, 24, 24);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: entityTypeColors[dept.type],
      transparent: true,
      opacity: 0.3,
      side: THREE.BackSide
    });

    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    group.add(glow);

    // Store materials for animation
    group.userData.materials = {
      sphere: material,
      glow: glowMaterial
    };

    return group;
  });
}, [entityTypeColors, safeOperation]);

// Enhanced animation loop with smooth controls
// Enhanced animation loop with performance optimizations
// ENHANCED ANIMATION LOOP WITH SEQUENCE LABEL BLINKING
const animate = useCallback(() => {
  try {
    animationRef.current = requestAnimationFrame(animate);

    const time = Date.now() * 0.001;
    const delta = Math.min(time - state.lastFrameTime, 0.1);
    
    if (state.isPlaying && state.rotationSpeed > 0 && controlsRef.current) {
      controlsRef.current.autoRotate = true;
      controlsRef.current.autoRotateSpeed = state.rotationSpeed * 0.5;
    }

    // Optimized particle animation
    if (state.showParticles && state.performance.fps > 30) {
      particlesRef.current.forEach((particle, index) => {
        if (particle.userData?.curve) {
          if (performanceMode === 'performance' && index % 3 !== 0) return;
          
          particle.userData.progress += particle.userData.speed * state.flowSpeed * delta;
          if (particle.userData.progress > 1) particle.userData.progress = 0;
          
          const position = particle.userData.curve.getPointAt(particle.userData.progress);
          particle.position.copy(position);
          
          const pulse = 0.6 + 0.4 * Math.sin(time * 6 + particle.userData.progress * 15);
          particle.scale.setScalar(pulse);
        }
      });
    }

    // ENHANCED: Animate sequence labels with harmonious blinking
    threadsRef.current.forEach((threadObj) => {
      if (threadObj.sequenceLabels) {
        threadObj.sequenceLabels.forEach((label, index) => {
          if (label.userData && label.material) {
            // Harmonious blinking with staggered phases
            const blinkPhase = label.userData.blinkPhase + time * label.userData.pulseSpeed;
            const blink = 0.7 + 0.3 * Math.sin(blinkPhase * Math.PI * 2);
            
            // Scale pulsation
            const scale = label.userData.baseScale * (0.9 + 0.1 * blink);
            label.scale.set(scale, scale, 1);
            
            // Opacity pulsation
            label.material.opacity = 0.8 + 0.2 * blink;
            
            // Color intensity variation
            if (label.material.color) {
              const intensity = 0.8 + 0.2 * blink;
              label.material.color.setRGB(
                label.userData.threadColor.r * intensity,
                label.userData.threadColor.g * intensity,
                label.userData.threadColor.b * intensity
              );
            }
            
            // Subtle floating animation
            const floatOffset = Math.sin(blinkPhase * 3) * 0.3;
            label.position.y = label.userData.originalPosition.y + floatOffset;
            
            // Rotate to always face camera
            if (cameraRef.current) {
              label.lookAt(cameraRef.current.position);
            }
          }
        });
      }
    });

    if (controlsRef.current) {
      controlsRef.current.update();
    }

    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    performanceMonitorRef.current.updatePerformanceMetrics();
    
    safeSetState(prev => ({ ...prev, lastFrameTime: time }));

  } catch (error) {
    console.warn('Enhanced animation loop warning:', error);
  }
}, [state.isPlaying, state.rotationSpeed, state.flowSpeed, state.showParticles, state.performance.fps, performanceMode, safeSetState]);


// SEQUENCE LABEL MANAGEMENT FUNCTIONS
const updateSequenceLabelsVisibility = useCallback((visible) => {
  safeOperation(() => {
    threadsRef.current.forEach((threadObj) => {
      if (threadObj.sequenceLabels) {
        threadObj.sequenceLabels.forEach(label => {
          label.visible = visible && state.showLabels;
        });
      }
    });
  }, null, 'updateSequenceLabelsVisibility');
}, [state.showLabels, safeOperation]);

const highlightSequenceFlow = useCallback((threadId) => {
  safeOperation(() => {
    const threadObj = threadsRef.current.get(threadId);
    if (!threadObj || !threadObj.sequenceLabels) return;

    // Highlight sequence labels in order
    threadObj.sequenceLabels.forEach((label, index) => {
      setTimeout(() => {
        if (label.material) {
          // Store original values
          const originalScale = label.scale.x;
          const originalOpacity = label.material.opacity;
          
          // Highlight effect
          label.scale.set(originalScale * 1.5, originalScale * 1.5, 1);
          label.material.opacity = 1.0;
          
          // Reset after highlight
          setTimeout(() => {
            label.scale.set(originalScale, originalScale, 1);
            label.material.opacity = originalOpacity;
          }, 500);
        }
      }, index * 300); // Staggered highlighting
    });
  }, null, 'highlightSequenceFlow');
}, [safeOperation]);


// WOW Effect Functions
const createGoldenThreadRevelation = useCallback((threadId) => {
  const thread = threadsData.find(t => t.id === threadId);
  if (!thread) return;

  // Create golden particle explosion
  const particleCount = 80;
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);

  const center = new THREE.Vector3(0, 0, 0);
  const goldenColor = new THREE.Color(0xffd700);

  for (let i = 0; i < particleCount; i++) {
    const i3 = i * 3;
    const angle = Math.random() * Math.PI * 2;
    const radius = 20 + Math.random() * 30;
    
    positions[i3] = Math.cos(angle) * radius;
    positions[i3 + 1] = (Math.random() - 0.5) * 40;
    positions[i3 + 2] = Math.sin(angle) * radius;
    
    const color = goldenColor.clone();
    color.offsetHSL((Math.random() - 0.5) * 0.2, 0, 0);
    colors[i3] = color.r;
    colors[i3 + 1] = color.g;
    colors[i3 + 2] = color.b;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  const material = new THREE.PointsMaterial({
    size: 4,
    vertexColors: true,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending
  });

  const particles = new THREE.Points(geometry, material);
  sceneRef.current.add(particles);

  // Animate golden explosion
  const startTime = Date.now();
  const duration = 2000;

  const animateExplosion = () => {
    const now = Date.now();
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    const currentPositions = particles.geometry.attributes.position.array;
    
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      currentPositions[i3] *= (1 - progress);
      currentPositions[i3 + 1] *= (1 - progress);
      currentPositions[i3 + 2] *= (1 - progress);
    }
    
    particles.geometry.attributes.position.needsUpdate = true;
    material.opacity = 1 - progress;
    
    if (progress < 1) {
      requestAnimationFrame(animateExplosion);
    } else {
      sceneRef.current.remove(particles);
    }
  };

  animateExplosion();
}, [threadsData]);

const createQuantumPulseWave = useCallback(() => {
  // Create a wave that pulses through all threads
  threadsRef.current.forEach((threadObj, index) => {
    setTimeout(() => {
      if (threadObj.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'thread-connection' && child.material) {
            // Store original opacity
            const originalOpacity = child.material.opacity;
            
            // Pulse effect
            child.material.opacity = originalOpacity * 1.5;
            setTimeout(() => {
              child.material.opacity = originalOpacity;
            }, 500);
          }
        });
      }
    }, index * 100); // Stagger the effect
  });
}, []);

// Enhanced user controls reset function
const resetViewToOptimal = useCallback(() => {
  if (cameraRef.current && controlsRef.current) {
    // Reset to optimal viewing position
    cameraRef.current.position.set(120, 80, 120);
    cameraRef.current.lookAt(0, 0, 0);
    
    // Reset controls target
    controlsRef.current.target.set(0, 0, 0);
    controlsRef.current.update();
    
    // Create reset wow effect
    createQuantumBloomEffect();
  }
}, []);

// Enhanced UI with better controls
const EnhancedControlPanel = () => (
  <div className="quantum-control-panel">
    <div className="panel-header">
      <h3>🌌 Quantum Digital Thread Explorer</h3>
      <button 
        className="hide-panel-btn"
        onClick={() => safeSetState(prev => ({ ...prev, showControls: false }))}
      >
        ✕
      </button>
    </div>
    
    <div className="control-section">
      <h4>🎮 Navigation Controls</h4>
      <div className="control-group">
        <button 
          className="control-btn"
          onClick={resetViewToOptimal}
          title="Reset to optimal view"
        >
          🎯 Reset View
        </button>
        <button 
          className={`control-btn ${state.isPlaying ? 'active' : ''}`}
          onClick={() => safeSetState(prev => ({ ...prev, isPlaying: !prev.isPlaying }))}
        >
          {state.isPlaying ? '⏸️ Pause' : '▶️ Play'}
        </button>
      </div>
      
      <div className="navigation-instructions">
        <p><strong>Mouse/Touch Controls:</strong></p>
        <p>• <strong>Left Drag:</strong> Rotate view</p>
        <p>• <strong>Right Drag:</strong> Pan view</p>
        <p>• <strong>Scroll/Middle Drag:</strong> Zoom</p>
        <p>• <strong>Click Threads:</strong> Select & explore</p>
      </div>
    </div>

    <div className="control-section">
      <h4>✨ Visual Effects</h4>
      <div className="control-group">
        <button 
          className={`control-btn ${state.showLabels ? 'active' : ''}`}
          onClick={() => safeSetState(prev => ({ ...prev, showLabels: !prev.showLabels }))}
        >
          {state.showLabels ? '🏷️ Hide Labels' : '🏷️ Show Labels'}
        </button>
        <button 
          className={`control-btn ${state.showParticles ? 'active' : ''}`}
          onClick={() => safeSetState(prev => ({ ...prev, showParticles: !prev.showParticles }))}
        >
          {state.showParticles ? '✨ Hide Particles' : '✨ Show Particles'}
        </button>
      </div>
    </div>

    <div className="control-section">
      <h4>🌟 Golden Threads</h4>
      <div className="threads-list">
        {threadsData.map(thread => (
          <div key={thread.id} className={`thread-item ${state.goldenThreads.has(thread.id) ? 'golden' : ''}`}>
            <label className="thread-toggle">
              <input
                type="checkbox"
                checked={state.visibleThreads.has(thread.id)}
                onChange={() => toggleThreadVisibility(thread.id)}
              />
              <span className="thread-color" style={{ backgroundColor: `#${thread.color.toString(16)}` }} />
              <span className="thread-name">
                {thread.name}
                {thread.critical && ' ⚡'}
              </span>
            </label>
            <button
              className={`golden-thread-btn ${state.goldenThreads.has(thread.id) ? 'active' : ''}`}
              onClick={() => toggleGoldenThread(thread.id)}
            >
              {state.goldenThreads.has(thread.id) ? '🌟' : '⭐'}
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);

  // Enhanced CSS additions for new features
  const enhancedControlStyles = `
    /* Bulk Controls */
    .bulk-controls {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      margin-bottom: 15px;
    }
    
    .bulk-btn {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid var(--event-horizon);
      border-radius: 6px;
      color: var(--quantum-primary);
      cursor: pointer;
      padding: 8px 12px;
      font-size: 11px;
      transition: all 0.3s ease;
    }
    
    .bulk-btn:hover {
      background: rgba(0, 243, 255, 0.1);
      transform: translateY(-1px);
    }
    
    /* Connected Stats */
    .connected-stats {
      display: flex;
      justify-content: space-between;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      padding: 12px;
    }
    
    .stat-item {
      text-align: center;
    }
    
    .stat-label {
      display: block;
      font-size: 10px;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 4px;
    }
    
    .stat-value {
      display: block;
      font-size: 1.2rem;
      font-weight: bold;
      color: var(--quantum-accent);
    }
    
    /* Enhanced Thread Items */
    .thread-item.hidden {
      opacity: 0.5;
      background: rgba(255, 255, 255, 0.02);
    }
    
    .thread-count {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 10px;
    }
  `;

  // Inject enhanced styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = enhancedControlStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Enhanced initialization on mount
// Enhanced initialization on mount
useEffect(() => {
  let mounted = true;
  let wowEffectCleanup;
  let performanceInterval;
  let lodInterval;

  const initialize = async () => {
    try {
      await initializeQuantumVisualization();
      
      if (mounted) {
        // Start animation
        animate();
        
        // Start continuous wow moments
        // wowEffectCleanup = startContinuousWowMoments();
        
        // Start performance optimization
        performanceInterval = setInterval(() => {
          const report = performanceMonitorRef.current.getPerformanceReport();
          
          if (report.current.fps < 30 && performanceMode !== 'performance') {
            setPerformanceMode('performance');
            optimizeVisualization('performance');
          } else if (report.current.fps > 50 && performanceMode === 'performance') {
            setPerformanceMode('balanced');
            optimizeVisualization('balanced');
          } else if (report.current.fps > 90 && performanceMode === 'balanced') {
            setPerformanceMode('high');
            optimizeVisualization('high');
          }
        }, 5000);
        
        // Start LOD optimization
        lodInterval = setInterval(() => {
          if (!cameraRef.current) return;

          const cameraPosition = cameraRef.current.position;
          let avgDistance = 0;
          let count = 0;

          departmentsRef.current.forEach(dept => {
            const distance = dept.position.distanceTo(cameraPosition);
            avgDistance += distance;
            count++;
          });

          if (count > 0) {
            avgDistance /= count;
            
            let newLodLevel = 'high';
            if (avgDistance > 150) newLodLevel = 'low';
            else if (avgDistance > 80) newLodLevel = 'medium';

            if (newLodLevel !== lodLevel) {
              updateThreadLOD(newLodLevel);
            }
          }
        }, 2000);
        
        // Add enhanced event listeners
        const handleKeyPress = (event) => {
          if (event.key === 'r' || event.key === 'R') {
            resetView();
          }
          if (event.key === ' ') {
            safeSetState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
          }
          if (event.key === 'a' && event.ctrlKey) {
            event.preventDefault();
            selectAllThreads();
          }
          if (event.key === 'd' && event.ctrlKey) {
            event.preventDefault();
            deselectAllThreads();
          }
        };

        window.addEventListener('keydown', handleKeyPress);
        
        console.log('Enhanced quantum visualization fully operational!');
      }
    } catch (error) {
      console.error('Enhanced initialization failed:', error);
    }
  };

  initialize();

  return () => {
    mounted = false;
    if (wowEffectCleanup) wowEffectCleanup();
    if (performanceInterval) clearInterval(performanceInterval);
    if (lodInterval) clearInterval(lodInterval);
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };
}, [initializeQuantumVisualization, animate, safeSetState, selectAllThreads, deselectAllThreads]);

// Initialize on mount with enhanced setup
useEffect(() => {
  let mounted = true;
  let wowEffectCleanup;

  const initialize = async () => {
    try {
      await initializeQuantumVisualization();
      
      if (mounted) {
        // Start animation
        animate();
        
        // Start continuous wow moments
       // wowEffectCleanup = startContinuousWowMoments();
        
        // Add enhanced event listeners
        const handleKeyPress = (event) => {
          if (event.key === 'r' || event.key === 'R') {
            resetViewToOptimal();
          }
          if (event.key === ' ') {
            safeSetState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
          }
        };

        window.addEventListener('keydown', handleKeyPress);
        
        console.log('Enhanced quantum visualization fully operational!');
      }
    } catch (error) {
      console.error('Enhanced initialization failed:', error);
    }
  };

  initialize();

  return () => {
    mounted = false;
    if (wowEffectCleanup) wowEffectCleanup();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };
}, [initializeQuantumVisualization, animate, resetViewToOptimal, safeSetState]);


  // Enhanced mouse move handler with advanced interactions
  const handleMouseMove = useCallback((event) => {
    safeOperation(() => {
      if (!cameraRef.current || !sceneRef.current || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update raycaster
      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

      // Find intersections with all interactive objects
      const intersectableObjects = [];
      departmentsRef.current.forEach(dept => intersectableObjects.push(dept));
      threadsRef.current.forEach(thread => {
        if (thread.group && thread.group.visible) {
          intersectableObjects.push(thread.group);
        }
      });

      const intersects = raycasterRef.current.intersectObjects(intersectableObjects, true);

      if (intersects.length > 0) {
        const intersect = intersects[0];
        const object = intersect.object;
        const userData = object.userData || object.parent?.userData;

        if (userData) {
          let tooltipContent = '';
          let elementData = null;
          let tooltipType = 'default';

          if (userData.type === 'department') {
            tooltipContent = tooltipManagerRef.current.getTooltipContent(userData.id);
            elementData = userData;
            tooltipType = 'department';
            
            // Enhanced department highlighting
            if (object.material && object.material.emissive) {
              object.material.emissive.setHex(0x444444);
              object.material.needsUpdate = true;
            }
          } else if (userData.type === 'thread-connection') {
            // Find the thread that owns this connection
            for (const [threadId, threadObj] of threadsRef.current.entries()) {
              if (threadObj.group.children.includes(object) || 
                  threadObj.group.children.some(child => child.children?.includes(object))) {
                tooltipContent = tooltipManagerRef.current.getTooltipContent(`thread-${threadId}`);
                elementData = threadObj.thread;
                tooltipType = 'thread';
                
                // Thread connection highlighting
                if (object.material) {
                  object.material.opacity = 0.9;
                  object.material.needsUpdate = true;
                }
                break;
              }
            }
          }

          if (tooltipContent) {
            safeSetState(prev => ({
              ...prev,
              tooltip: {
                visible: true,
                content: tooltipContent,
                x: event.clientX,
                y: event.clientY,
                elementData: elementData,
                type: tooltipType
              }
            }));
            return;
          }
        }
      } else {
        // Reset all highlights when not hovering
        departmentsRef.current.forEach(dept => {
          if (dept.children[0]?.material?.emissive && dept.userData?.originalColor) {
            dept.children[0].material.emissive.setHex(dept.userData.originalColor);
            dept.children[0].material.needsUpdate = true;
          }
        });
        
        threadsRef.current.forEach(threadObj => {
          if (threadObj.group) {
            threadObj.group.children.forEach(child => {
              if (child.userData?.type === 'thread-connection' && child.material) {
                const isSelected = state.selectedThreads.has(threadObj.thread.id);
                child.material.opacity = isSelected ? 0.9 : 0.6;
                child.material.needsUpdate = true;
              }
            });
          }
        });
      }

      // Hide tooltip if no intersections
      safeSetState(prev => ({
        ...prev,
        tooltip: { ...prev.tooltip, visible: false }
      }));

    }, null, 'handleMouseMove');
  }, [safeSetState, state.selectedThreads, safeOperation]);

  // Enhanced mouse click handler with advanced interactions
  const handleMouseClick = useCallback((event) => {
    safeOperation(() => {
      if (!cameraRef.current || !sceneRef.current || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

      const intersectableObjects = [];
      departmentsRef.current.forEach(dept => intersectableObjects.push(dept));
      threadsRef.current.forEach(thread => {
        if (thread.group && thread.group.visible) {
          intersectableObjects.push(thread.group);
        }
      });

      const intersects = raycasterRef.current.intersectObjects(intersectableObjects, true);

      if (intersects.length > 0) {
        const intersect = intersects[0];
        const object = intersect.object;
        const userData = object.userData || object.parent?.userData;

        if (userData) {
          if (userData.type === 'thread-connection') {
            // Find and select the thread
            for (const [threadId, threadObj] of threadsRef.current.entries()) {
              if (threadObj.group.children.includes(object) || 
                  threadObj.group.children.some(child => child.children?.includes(object))) {
                toggleThreadSelection(threadId);
                createQuantumWowMoment(threadId);
                
                // Show thread analytics
                safeSetState(prev => ({
                  ...prev,
                  currentSelection: {
                    type: 'thread',
                    data: threadObj.thread
                  },
                  showAnalyticsPanel: true
                }));
                break;
              }
            }
          } else if (userData.type === 'department') {
            // Department clicked - show department analytics
            safeSetState(prev => ({
              ...prev,
              currentSelection: {
                type: 'department',
                data: userData
              },
              showAnalyticsPanel: true
            }));
            createQuantumWowMoment();
          }
        }
      } else {
        // Clicked on empty space - clear selection
        safeSetState(prev => ({
          ...prev,
          currentSelection: null
        }));
      }
    }, null, 'handleMouseClick');
  }, [createQuantumWowMoment, safeSetState, safeOperation]);

  // Enhanced thread selection function
  // Enhanced thread selection function - UPDATED
 // ENHANCED THREAD SELECTION WITH VISUAL FEEDBACK
const toggleThreadSelection = useCallback((threadId) => {
  safeSetState(prev => {
    const newSelectedThreads = new Set(prev.selectedThreads);
    const wasSelected = newSelectedThreads.has(threadId);
    
    if (wasSelected) {
      newSelectedThreads.delete(threadId);
    } else {
      newSelectedThreads.add(threadId);
      // Create visual feedback for selection
      createQuantumWowMoment(threadId);
    }

    // Update thread visualization with enhanced highlighting
    threadsRef.current.forEach((threadObj, id) => {
      if (threadObj?.group) {
        threadObj.group.children.forEach(child => {
          if (child.userData?.type === 'thread-connection' && child.material) {
            if (newSelectedThreads.has(id)) {
              // Selected state - enhanced highlighting
              child.material.color.setHex(entityTypeColors.selected);
              child.material.opacity = 0.9;
              if (child.material.linewidth !== undefined) {
                child.material.linewidth = 4;
              }
              
              // Add pulsing effect
              child.userData.originalOpacity = 0.9;
              child.userData.pulsePhase = 0;
            } else {
              // Normal state - restore original appearance
              const originalColor = threadObj.isGolden ? 
                entityTypeColors.golden : 
                (typeof threadObj.thread.color === 'number' ? threadObj.thread.color : 0x45b7d1);
              child.material.color.setHex(originalColor);
              child.material.opacity = 0.6;
              if (child.material.linewidth !== undefined) {
                child.material.linewidth = 2;
              }
            }
            child.material.needsUpdate = true;
          }
        });
      }
    });

    return { 
      ...prev, 
      selectedThreads: newSelectedThreads,
      // Auto-show analytics panel when selecting threads
      showAnalyticsPanel: newSelectedThreads.size > 0
    };
  });
}, [entityTypeColors, safeSetState, createQuantumWowMoment]);

 // Enhanced thread visibility function - CORRECTED
const toggleThreadVisibility = useCallback((threadId) => {
  safeSetState(prev => {
    const newVisibleThreads = new Set(prev.visibleThreads);
    if (newVisibleThreads.has(threadId)) {
      newVisibleThreads.delete(threadId);
    } else {
      newVisibleThreads.add(threadId);
    }
    
    const threadObj = threadsRef.current.get(threadId);
    if (threadObj?.group) {
      threadObj.group.visible = newVisibleThreads.has(threadId);
      if (newVisibleThreads.has(threadId)) {
        createQuantumWowMoment(threadId);
      }
    }
    
    // Update department visibility when threads change
    updateDepartmentVisibility();
    
    return { ...prev, visibleThreads: newVisibleThreads };
  });
}, [createQuantumWowMoment, safeSetState, updateDepartmentVisibility]);

  // Enhanced golden thread management
// Enhanced golden thread management - CORRECTED
const toggleGoldenThread = useCallback((threadId) => {
  safeSetState(prev => {
    const newGoldenThreads = new Set(prev.goldenThreads);
    if (newGoldenThreads.has(threadId)) {
      newGoldenThreads.delete(threadId);
    } else {
      newGoldenThreads.add(threadId);
      createQuantumWowMoment(threadId);
    }
    
    // Update thread visualization with golden effects
    const threadObj = threadsRef.current.get(threadId);
    if (threadObj) {
      const isGolden = newGoldenThreads.has(threadId);
      const newColor = isGolden ? entityTypeColors.golden : 
        (typeof threadObj.thread.color === 'number' ? threadObj.thread.color : 0x45b7d1);
      
      threadObj.group.children.forEach(child => {
        if (child.userData?.type === 'thread-connection' && child.material) {
          if (!prev.selectedThreads.has(threadId)) {
            child.material.color = new THREE.Color(newColor);
            child.material.needsUpdate = true;
          }
        }
        if (child.userData?.type === 'data-particle' && child.material) {
          child.material.color = new THREE.Color(newColor);
          child.material.needsUpdate = true;
        }
      });
      
      threadObj.isGolden = isGolden;
    }
    
    return { ...prev, goldenThreads: newGoldenThreads };
  });
}, [createQuantumWowMoment, entityTypeColors, safeSetState]);

  // Handle window resize with debouncing
  const handleResize = useCallback(() => {
    safeOperation(() => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
      
      if (composerRef.current) {
        composerRef.current.setSize(width, height);
      }
    }, null, 'handleResize');
  }, [safeOperation]);

  // Enhanced full screen functionality
  const toggleFullScreen = useCallback(() => {
    safeOperation(() => {
      if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen?.().catch(console.error);
        safeSetState(prev => ({ ...prev, isFullScreen: true }));
      } else {
        document.exitFullscreen?.().catch(console.error);
        safeSetState(prev => ({ ...prev, isFullScreen: false }));
      }
    }, null, 'toggleFullScreen');
  }, [safeSetState, safeOperation]);

  // Initialize on mount with comprehensive setup
  useEffect(() => {
    let mounted = true;
    let initializationTimeout;

    const initialize = async () => {
      try {
        await initializeQuantumVisualization();
        
        if (mounted) {
          // Start animation loop
          animate();
          
// Add enhanced event listeners
const resizeHandler = () => handleResize();
const mouseMoveHandler = (e) => handleMouseMove(e);
const clickHandler = (e) => handleEnhancedMouseClick(e);
const doubleClickHandler = (e) => handleDoubleClick(e);

window.addEventListener('resize', resizeHandler);
if (containerRef.current) {
  containerRef.current.addEventListener('mousemove', mouseMoveHandler);
  containerRef.current.addEventListener('click', clickHandler);
  containerRef.current.addEventListener('dblclick', doubleClickHandler);
}

// Store for cleanup
eventListenersRef.current = [
  { type: 'resize', handler: resizeHandler, target: window },
  { type: 'mousemove', handler: mouseMoveHandler, target: containerRef.current },
  { type: 'click', handler: clickHandler, target: containerRef.current },
  { type: 'dblclick', handler: doubleClickHandler, target: containerRef.current }
];



          // Add ResizeObserver for container changes
          if (containerRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
              handleResize();
            });
            resizeObserverRef.current.observe(containerRef.current);
          }

          console.log('Quantum visualization component fully mounted');
        }
      } catch (error) {
        console.error('Component initialization failed:', error);
        safeSetState({ 
          hasError: true, 
          errorMessage: `Component initialization failed: ${error.message}` 
        });
      }
    };

    // Start initialization with a small delay to ensure DOM is ready
    initializationTimeout = setTimeout(initialize, 100);

    // Comprehensive cleanup function
    return () => {
      mounted = false;
      clearTimeout(initializationTimeout);
      
      // Stop animation loop
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      // Stop performance monitoring
      performanceMonitorRef.current.stopMonitoring();
      
      // Remove event listeners
      eventListenersRef.current.forEach(({ type, handler, target }) => {
        if (target && target.removeEventListener) {
          target.removeEventListener(type, handler);
        }
      });
      eventListenersRef.current = [];
      
      // Disconnect ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      
      // Comprehensive Three.js cleanup
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (composerRef.current) {
        composerRef.current.dispose();
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      
      // Clear references
      sceneRef.current = null;
      rendererRef.current = null;
      composerRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
      
      console.log('Quantum visualization component fully cleaned up');
    };
  }, [
    initializeQuantumVisualization, 
    animate, 
    handleResize, 
    handleMouseMove, 
    handleMouseClick, 
    safeSetState
  ]);

  // Real-time updates for visualization controls
  useEffect(() => {
    safeOperation(() => {
      departmentsRef.current.forEach(dept => {
        const label = dept.children.find(child => child.userData?.isLabel);
        if (label) {
          label.visible = state.showLabels;
        }
      });

      threadsRef.current.forEach(threadObj => {
        if (threadObj.group) {
          threadObj.group.children.forEach(child => {
            if (child.userData?.type === 'sequence-label') {
              child.visible = state.showLabels;
            }
          });
        }
      });
    }, null, 'updateLabelsVisibility');
  }, [state.showLabels, safeOperation]);

  useEffect(() => {
    safeOperation(() => {
      particlesRef.current.forEach(particle => {
        particle.visible = state.showParticles;
      });
    }, null, 'updateParticlesVisibility');
  }, [state.showParticles, safeOperation]);

  // Enhanced Quantum Control Panel with new features
// Enhanced Quantum Control Panel Component
// ENHANCED QUANTUM CONTROL PANEL WITH THREAD MANAGEMENT
const QuantumControlPanel = () => {
  const threadControlCollapsed = !state.expandedSections.threadManagement;
  const zoomControlsCollapsed = !state.expandedSections.quantumNavigation;
  const visualizationCollapsed = !state.expandedSections.quantumControls;
  const layoutCollapsed = !state.expandedSections.layoutManagement;
  
  const connectedEntities = getConnectedEntities();
  
  // Helper function to toggle sections
  const toggleSection = useCallback((sectionName) => {
    safeSetState(prev => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        [sectionName]: !prev.expandedSections[sectionName]
      }
    }));
  }, [safeSetState]);

  
  // Thread selection handler
  const handleThreadSelect = useCallback((threadId) => {
    safeSetState(prev => {
      const newSelectedThreads = new Set(prev.selectedThreads);
      if (newSelectedThreads.has(threadId)) {
        newSelectedThreads.delete(threadId);
      } else {
        newSelectedThreads.add(threadId);
        createQuantumWowMoment(threadId);
      }
      return { ...prev, selectedThreads: newSelectedThreads };
    });
  }, [safeSetState, createQuantumWowMoment]);
 
 // Toggle selected threads visibility
const toggleSelectedThreadsVisibility = useCallback((makeVisible = true) => {
  safeSetState(prev => {
    const newVisibleThreads = new Set(prev.visibleThreads);
    
    // Update selected threads visibility
    prev.selectedThreads.forEach(threadId => {
      const threadObj = threadsRef.current.get(threadId);
      if (threadObj?.group) {
        threadObj.group.visible = makeVisible;
        if (makeVisible) {
          newVisibleThreads.add(threadId);
        } else {
          newVisibleThreads.delete(threadId);
        }
      }
    });
    
    // Update department visibility
    updateDepartmentVisibility();
    
    // Create visual feedback
    createQuantumBloomEffect(1.0, 400);
    
    return {
      ...prev,
      visibleThreads: newVisibleThreads
    };
  });
}, [updateDepartmentVisibility, createQuantumBloomEffect, safeSetState]);

 
  // Apply optimized layout
  const applyOptimizedLayout = useCallback(async () => {
    if (state.selectedThreads.size === 0) {
      alert('Please select threads to optimize');
      return;
    }
    
    const selectedThreadsArray = Array.from(state.selectedThreads);
    await transformToLayout('optimized', selectedThreadsArray);
    
    // Create visual feedback for optimization
    createQuantumBloomEffect(3.0, 1000);
    safeSetState(prev => ({ 
      ...prev, 
      showAnalyticsPanel: true 
    }));
  }, [state.selectedThreads, transformToLayout, createQuantumBloomEffect, safeSetState]);
  
  // Clear thread selection
  const clearThreadSelection = useCallback(() => {
    safeSetState(prev => ({ ...prev, selectedThreads: new Set() }));
  }, [safeSetState]);
  
  return (
    <div className="quantum-control-panel">
      <div className="panel-header">
        <h3>🌌 Quantum Digital Thread Explorer</h3>
        <button 
          className="hide-panel-btn"
          onClick={() => safeSetState(prev => ({ ...prev, showControls: false }))}
          title="Hide quantum control panel"
        >
          ✕
        </button>
      </div>
      
{/* 🧵 THREAD MANAGEMENT SECTION */}
<div className="control-section">
  <div className="section-header" onClick={() => toggleSection('threadManagement')}>
    <h4>🧵 Thread Management</h4>
    <span className="expand-icon">
      {!state.expandedSections.threadManagement ? '▶' : '▼'}
    </span>
  </div>
  
  {state.expandedSections.threadManagement && (
    <div className="thread-management-container">
      {/* VISIBILITY CONTROLS - TOP SECTION */}
      <div className="visibility-controls-panel">
        <div className="visibility-header">
          <h5>🔍 Visibility Controls</h5>
          <div className="visibility-stats">
            <span className="stat-item">
              <span className="stat-label">Visible</span>
              <span className="stat-value visible-count">{state.visibleThreads.size}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Hidden</span>
              <span className="stat-value hidden-count">{threadsData.length - state.visibleThreads.size}</span>
            </span>
            <span className="stat-item">
              <span className="stat-label">Selected</span>
              <span className="stat-value selected-count">{state.selectedThreads.size}</span>
            </span>
          </div>
        </div>
        
        {/* Quick Visibility Actions */}
        <div className="visibility-quick-actions">
          <div className="visibility-action-row">
            <button 
              className="visibility-action-btn show-all-btn"
              onClick={() => toggleAllThreadsVisibility(true)}
              title="Show all threads"
            >
              👁️ Show All
            </button>
            <button 
              className="visibility-action-btn hide-all-btn"
              onClick={() => toggleAllThreadsVisibility(false)}
              title="Hide all threads"
            >
              🙈 Hide All
            </button>
            <button 
              className="visibility-action-btn invert-btn"
              onClick={invertThreadVisibility}
              title="Invert visibility"
            >
              🔄 Invert
            </button>
          </div>
          
          <div className="visibility-action-row">
            <button 
              className="visibility-action-btn show-golden-btn"
              onClick={() => showThreadsByType('golden')}
              title="Show only golden threads"
            >
              🌟 Golden
            </button>
            <button 
              className="visibility-action-btn show-critical-btn"
              onClick={() => showThreadsByType('critical')}
              title="Show only critical threads"
            >
              ⚡ Critical
            </button>
            <button 
              className="visibility-action-btn show-value-btn"
              onClick={() => showThreadsByType('value')}
              title="Show only value streams"
            >
              💎 Value
            </button>
          </div>
          
          <div className="visibility-action-row">
            <button 
              className="visibility-action-btn show-selected-btn"
              onClick={showOnlySelectedThreads}
              disabled={state.selectedThreads.size === 0}
              title="Show only selected threads"
            >
              ✅ Show Selected
            </button>
            <button 
              className="visibility-action-btn hide-selected-btn"
              onClick={() => {
                toggleSelectedThreadsVisibility(false);
              }}
              disabled={state.selectedThreads.size === 0}
              title="Hide selected threads"
            >
              ❌ Hide Selected
            </button>
            <button 
              className="visibility-action-btn show-healthy-btn"
              onClick={() => showThreadsByType('healthy')}
              title="Show only healthy threads (≥80%)"
            >
              💚 Healthy
            </button>
          </div>
        </div>
        
        {/* Filter Toggle */}
        <div className="visibility-filter">
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={state.showOnlyConnectedEntities}
              onChange={toggleFilterMode}
              className="filter-checkbox"
            />
            <span className="filter-label">
              🔗 Connected Entities Only
              <span className="filter-description">
                (Hides unconnected departments)
              </span>
            </span>
          </label>
        </div>
      </div>
      
      {/* SELECTION CONTROLS */}
      <div className="selection-controls-panel">
        <div className="selection-header">
          <h5>🎯 Selection Controls</h5>
          <div className="selection-stats">
            <span className="selected-count-display">
              {state.selectedThreads.size} selected
            </span>
          </div>
        </div>
        
        <div className="selection-quick-actions">
          <div className="selection-action-row">
            <button 
              className="selection-action-btn select-all-btn"
              onClick={selectAllThreads}
              title="Select all visible threads"
            >
              ✓ Select All
            </button>
            <button 
              className="selection-action-btn deselect-all-btn"
              onClick={deselectAllThreads}
              disabled={state.selectedThreads.size === 0}
              title="Deselect all threads"
            >
              ✗ Deselect All
            </button>
            <button 
              className="selection-action-btn select-visible-btn"
              onClick={() => {
                // Select all currently visible threads
                const visibleThreads = new Set(state.visibleThreads);
                safeSetState(prev => ({ ...prev, selectedThreads: visibleThreads }));
              }}
              title="Select all visible threads"
            >
              👁️ Select Visible
            </button>
          </div>
          
          <div className="selection-action-row">
            <button 
              className="selection-action-btn optimize-btn"
              onClick={applyOptimizedLayout}
              disabled={state.selectedThreads.size === 0}
              title="Apply optimized layout for selected threads"
            >
              ⚡ Optimize Layout
            </button>
            <button 
              className="selection-action-btn clear-btn"
              onClick={clearThreadSelection}
              disabled={state.selectedThreads.size === 0}
              title="Clear all selections"
            >
              🗑️ Clear
            </button>
            <button 
              className="selection-action-btn zoom-to-selected-btn"
              onClick={() => {
                if (state.selectedThreads.size > 0) {
                  const firstThreadId = Array.from(state.selectedThreads)[0];
                  zoomToEntityUltra(firstThreadId, 'thread', 3);
                }
              }}
              disabled={state.selectedThreads.size === 0}
              title="Zoom to first selected thread"
            >
              🔍 Zoom To
            </button>
          </div>
        </div>
      </div>
      
      {/* THREAD LIST */}
      <div className="thread-list-container">
        <div className="thread-list-header">
          <div className="list-header-left">
            <span className="thread-list-title">All Threads ({threadsData.length})</span>
            <span className="thread-list-stats">
              {getConnectedEntities().functions.size} functions • 
              {getConnectedEntities().domains.size} domains • 
              {getConnectedEntities().systems.size} systems
            </span>
          </div>
          <div className="list-header-right">
            <button 
              className="list-action-btn collapse-all-btn"
              onClick={() => {
                // Collapse all expanded thread details if implemented
              }}
              title="Collapse all thread details"
            >
              🗂️ Collapse All
            </button>
          </div>
        </div>
        
        {/* Search and Filter */}
        <div className="thread-list-search">
          <input
            type="text"
            placeholder="🔍 Search threads..."
            className="thread-search-input"
            onChange={(e) => {
              // Implement search functionality
            }}
          />
          <select className="thread-sort-select">
            <option value="name">Sort by Name</option>
            <option value="health">Sort by Health</option>
            <option value="connections">Sort by Connections</option>
            <option value="critical">Critical First</option>
            <option value="golden">Golden First</option>
          </select>
        </div>
        
        {/* Thread List Items */}
        <div className="threads-list-scroll">
          {threadsData.map(thread => {
            const isSelected = state.selectedThreads.has(thread.id);
            const isVisible = state.visibleThreads.has(thread.id);
            const isGolden = state.goldenThreads.has(thread.id);
            
            return (
              <div 
                key={thread.id}
                className={`thread-item 
                  ${isSelected ? 'selected' : ''}
                  ${isGolden ? 'golden' : ''}
                  ${thread.critical ? 'critical' : ''}
                  ${!isVisible ? 'hidden' : ''}
                  ${thread.valueStream ? 'value-stream' : ''}`}
                onClick={() => handleThreadSelect(thread.id)}
              >
                {/* Visibility Toggle (Eye Icon) */}
                <div 
                  className="thread-visibility-toggle"
                  onClick={(e) => toggleSingleThreadVisibility(thread.id, e)}
                  title={isVisible ? "Click to hide thread" : "Click to show thread"}
                >
                  {isVisible ? '👁️' : '👁️‍🗨️'}
                </div>
                
                {/* Thread Color Indicator */}
                <div className="thread-color-indicator">
                  <div 
                    className="thread-color-dot"
                    style={{ 
                      backgroundColor: isGolden ? 
                        '#ffd700' : 
                        `#${(typeof thread.color === 'number' ? thread.color : 0x45b7d1).toString(16).padStart(6, '0')}`
                    }}
                  />
                </div>
                
                {/* Thread Info */}
                <div className="thread-info">
                  <div className="thread-name-row">
                    <span className="thread-name">{thread.name}</span>
                    <div className="thread-badges">
                      {isGolden && <span className="golden-badge" title="Golden Thread">🌟</span>}
                      {thread.critical && <span className="critical-badge" title="Critical Thread">⚡</span>}
                      {thread.valueStream && <span className="value-badge" title="Value Stream">💎</span>}
                      {!isVisible && <span className="hidden-badge" title="Hidden">👁️</span>}
                      {thread.healthScore >= 80 && <span className="healthy-badge" title="Excellent Health">💚</span>}
                      {thread.healthScore < 60 && <span className="unhealthy-badge" title="Poor Health">❤️</span>}
                    </div>
                  </div>
                  
                  {/* Thread Description */}
                  <div className="thread-description">
                    {thread.description || `Connects ${thread.connects?.length || 0} functions, ${thread.domains?.length || 0} domains`}
                  </div>
                </div>
                
                {/* Selection Checkbox */}
                <div className="thread-checkbox" title={isSelected ? "Deselect" : "Select"}>
                  <div className={`checkbox-indicator ${isSelected ? 'checked' : ''}`}>
                    {isSelected ? '✓' : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* List Footer */}
        <div className="thread-list-footer">
          <div className="footer-stats">
            <span className="footer-stat">
              🟢 Healthy: {threadsData.filter(t => t.healthScore >= 80).length}
            </span>
            <span className="footer-stat">
              🟡 Moderate: {threadsData.filter(t => t.healthScore >= 60 && t.healthScore < 80).length}
            </span>
            <span className="footer-stat">
              🔴 Poor: {threadsData.filter(t => t.healthScore < 60).length}
            </span>
          </div>
          <div className="footer-actions">
            <button 
              className="footer-action-btn export-btn"
              onClick={() => {
                // Export thread data functionality
              }}
              title="Export thread data"
            >
              📥 Export
            </button>
          </div>
        </div>
      </div>
    </div>
  )}
</div>

      
      {/* 🎨 LAYOUT SELECTOR SECTION */}
      <div className="control-section">
<div className="section-header" onClick={() => toggleSection('layoutManagement')}>
          <h4>🎨 Layout Management</h4>
          <span className="expand-icon">
            {layoutCollapsed ? '▶' : '▼'}
          </span>
        </div>
        
{state.expandedSections.layoutManagement && (

          <div className="layout-management-container">
            {/* Layout Stats */}
            <div className="layout-stats">
              <div className="stat-item">
                <span className="stat-label">Current Layout</span>
                <span className="stat-value">{currentLayout}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Transforming</span>
                <span className="stat-value">{isTransforming ? 'Yes' : 'No'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Threads</span>
                <span className="stat-value">{state.selectedThreads.size}</span>
              </div>
            </div>
            
            {/* Layout Actions */}
            <div className="layout-actions">
              <button 
                className="layout-action-btn optimize-layout-btn"
                onClick={applyOptimizedLayout}
                disabled={state.selectedThreads.size === 0 || isTransforming}
              >
                ⚡ Optimize Layout
              </button>
              
              <button 
                className="layout-action-btn reset-layout-btn"
                onClick={resetToDefaultLayout}
                disabled={isTransforming}
              >
                🔄 Reset Layout
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* 🔍 ZOOM CONTROLS SECTION */}
      <div className="control-section">
 <div className="section-header" onClick={() => toggleSection('quantumNavigation')}>
          <h4>🔍 Navigation Controls</h4>
          <span className="expand-icon">
            {zoomControlsCollapsed ? '▶' : '▼'}
          </span>
        </div>
        
{state.expandedSections.quantumNavigation && (
          <div className="zoom-controls-container">
            {/* Zoom Presets */}
            <div className="zoom-presets">
              <button className="zoom-preset-btn" onClick={() => zoomTo10x()}>
                🔍 10x Ultra
              </button>
              <button className="zoom-preset-btn" onClick={() => zoomTo5x()}>
                👁️ 5x High
              </button>
              <button className="zoom-preset-btn" onClick={() => zoomToOverview()}>
                🌐 Overview
              </button>
              <button className="zoom-preset-btn" onClick={resetView}>
                🎯 Reset View
              </button>
            </div>
            
            {/* Zoom Slider */}
            <div className="zoom-slider-container">
              <label>Zoom Level: {state.zoomLevel.toFixed(1)}x</label>
              <input
                type="range"
                min="0.1"
                max="10"
                step="0.1"
                value={state.zoomLevel}
                onChange={(e) => {
                  const newZoomLevel = parseFloat(e.target.value);
                  safeSetState(prev => ({ ...prev, zoomLevel: newZoomLevel }));
                  updateZoomPerformance(newZoomLevel);
                  updateTextScaling(newZoomLevel);
                }}
                className="zoom-slider"
              />
              <div className="zoom-ticks">
                <span>0.1x</span>
                <span>1x</span>
                <span>5x</span>
                <span>10x</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* ⚡ VISUALIZATION CONTROLS */}
      <div className="control-section">
<div className="section-header" onClick={() => toggleSection('quantumControls')}>

          <h4>⚡ Visualization Controls</h4>
          <span className="expand-icon">
            {visualizationCollapsed ? '▶' : '▼'}
          </span>
        </div>
        
{state.expandedSections.quantumControls && (
          <div className="visualization-controls">
            <div className="toggle-group">
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={state.showLabels}
                  onChange={() => safeSetState(prev => ({ ...prev, showLabels: !prev.showLabels }))}
                />
                <span className="toggle-label">🏷️ Labels</span>
              </label>
              
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={state.showParticles}
                  onChange={() => safeSetState(prev => ({ ...prev, showParticles: !prev.showParticles }))}
                />
                <span className="toggle-label">✨ Particles</span>
              </label>
              
              <label className="toggle-item">
                <input
                  type="checkbox"
                  checked={state.showOnlyConnectedEntities}
                  onChange={toggleFilterMode}
                />
                <span className="toggle-label">🔗 Connected Only</span>
              </label>
            </div>
            
            {/* Speed Controls */}
            <div className="speed-controls">
              <div className="slider-group">
                <label>Flow Speed: {state.flowSpeed.toFixed(1)}</label>
                <input
                  type="range"
                  min="0.1"
                  max="3"
                  step="0.1"
                  value={state.flowSpeed}
                  onChange={(e) => safeSetState(prev => ({ ...prev, flowSpeed: parseFloat(e.target.value) }))}
                />
              </div>
              
              <div className="slider-group">
                <label>Rotation: {state.rotationSpeed.toFixed(1)}</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={state.rotationSpeed}
                  onChange={(e) => safeSetState(prev => ({ ...prev, rotationSpeed: parseFloat(e.target.value) }))}
                />
              </div>
            </div>
            
            {/* Play/Pause */}
            <button 
              className={`play-pause-btn ${state.isPlaying ? 'paused' : 'playing'}`}
              onClick={() => safeSetState(prev => ({ ...prev, isPlaying: !prev.isPlaying }))}
            >
              {state.isPlaying ? '⏸️ Pause' : '▶️ Play'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
// Then UPDATE the render section around line ~4410 to use the enhanced panel:
{state.showControls && <QuantumControlPanel />}

// Thread Statistics Function
const calculateThreadStats = useCallback(() => {
  const selectedThreadsArray = Array.from(state.selectedThreads);
  const selectedThreadsData = threadsData.filter(t => state.selectedThreads.has(t.id));
  
  const totalConnections = selectedThreadsData.reduce((sum, t) => {
    return sum + (t.connects?.length || 0) + (t.domains?.length || 0) + (t.systems?.length || 0);
  }, 0);
  
  const avgHealth = selectedThreadsData.length > 0 
    ? Math.round(selectedThreadsData.reduce((sum, t) => sum + (t.healthScore || 0), 0) / selectedThreadsData.length)
    : 0;
    
  return {
    selectedCount: selectedThreadsArray.length,
    totalConnections,
    avgHealth,
    goldenCount: selectedThreadsData.filter(t => state.goldenThreads.has(t.id)).length,
    criticalCount: selectedThreadsData.filter(t => t.critical).length
  };
}, [state.selectedThreads, state.goldenThreads, threadsData]);

// Optimize Zoom Performance
/*
const updateZoomPerformance = useCallback((zoomLevel) => {
  safeOperation(() => {
    // Dynamic performance settings based on zoom level
    if (zoomLevel > 5) {
      // Maximum zoom - highest quality
      setPerformanceMode('high');
      updateThreadLOD('high');
      safeSetState(prev => ({ 
        ...prev, 
        showParticles: true,
        showLabels: true 
      }));
    } else if (zoomLevel > 2) {
      // Medium zoom - balanced quality
      setPerformanceMode('balanced');
      updateThreadLOD('medium');
    } else {
      // Far zoom - performance mode
      setPerformanceMode('performance');
      updateThreadLOD('low');
      safeSetState(prev => ({ 
        ...prev, 
        showParticles: zoomLevel > 0.5 // Hide particles at very far zoom
      }));
    }
    
    optimizeVisualization(performanceMode);
    
  }, null, 'updateZoomPerformance');
}, [setPerformanceMode, updateThreadLOD, optimizeVisualization, safeSetState, safeOperation]);
*/

  const QuantumAnalyticsPanel = () => {
    const analytics = quantumEngineRef.current.getSessionAnalytics('current') || {};
    const recommendations = quantumEngineRef.current.getOptimizationRecommendations(threadsData);
    
    return (
      <div className="quantum-analytics-panel">
        <div className="panel-header">
          <h3>📊 Quantum Analytics</h3>
          <button 
            onClick={() => safeSetState(prev => ({ ...prev, showAnalyticsPanel: false }))}
            className="hide-panel-btn"
          >
            ✕
          </button>
        </div>
        
        <div className="analytics-content">
          <div className="analytics-section">
            <h4>System Overview</h4>
            <div className="stats-grid">
              <div className="stat">
                <span className="value">{threadsData.length}</span>
                <span className="label">Quantum Threads</span>
              </div>
              <div className="stat">
                <span className="value">{state.goldenThreads.size}</span>
                <span className="label">Golden Threads</span>
              </div>
              <div className="stat">
                <span className="value">{departmentsData.length}</span>
                <span className="label">Quantum Entities</span>
              </div>
              <div className="stat">
                <span className="value">{state.performance.fps}</span>
                <span className="label">Quantum FPS</span>
              </div>
            </div>
          </div>

          <div className="analytics-section">
            <h4>Thread Health</h4>
            <div className="health-list">
              {threadsData.map(thread => (
                <div key={thread.id} className="health-item">
                  <span className="thread-name">{thread.name}</span>
                  <div className="health-bar">
                    <div 
                      className={`health-fill ${thread.healthScore > 80 ? 'excellent' : thread.healthScore > 60 ? 'good' : 'poor'}`}
                      style={{ width: `${thread.healthScore}%` }}
                    ></div>
                  </div>
                  <span className="health-score">{thread.healthScore}%</span>
                </div>
              ))}
            </div>
          </div>

          {recommendations.length > 0 && (
            <div className="analytics-section">
              <h4>🔧 Optimization Recommendations</h4>
              <div className="recommendations-list">
                {recommendations.slice(0, 3).map((rec, index) => (
                  <div key={index} className={`recommendation ${rec.priority}`}>
                    <div className="rec-header">
                      <span className="rec-title">{rec.threadName}</span>
                      <span className={`rec-priority ${rec.priority}`}>{rec.priority}</span>
                    </div>
                    <p className="rec-message">{rec.message}</p>
                    <div className="rec-actions">
                      {rec.actions.slice(0, 2).map((action, i) => (
                        <span key={i} className="rec-action">{action}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {state.currentSelection && (
            <div className="analytics-section">
              <h4>Selected: {state.currentSelection.data.name}</h4>
              <div className="selection-details">
                <p><strong>Type:</strong> {state.currentSelection.data.type}</p>
                <p><strong>Layer:</strong> {state.currentSelection.data.layer}</p>
                {state.currentSelection.data.healthScore && (
                  <p><strong>Health:</strong> {state.currentSelection.data.healthScore}%</p>
                )}
                {state.currentSelection.data.optimizationPotential && (
                  <p><strong>Optimization Potential:</strong> {state.currentSelection.data.optimizationPotential}%</p>
                )}
                {state.currentSelection.data.connections && (
                  <p><strong>Connections:</strong> {state.currentSelection.data.connections}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const PerformancePanel = () => {
    const performanceReport = performanceMonitorRef.current.getPerformanceReport();
    
    return (
      <div className="performance-panel">
        <div className="panel-header">
          <h3>⚡ Performance Metrics</h3>
        </div>
        <div className="performance-content">
          <div className="performance-metric">
            <span className="metric-label">FPS</span>
            <span className="metric-value">{performanceReport.current.fps}</span>
            <span className="metric-trend">
              Avg: {performanceReport.averages.fps} | Min: {performanceReport.min.fps} | Max: {performanceReport.max.fps}
            </span>
          </div>
          <div className="performance-metric">
            <span className="metric-label">Frame Time</span>
            <span className="metric-value">{performanceReport.current.frameTime.toFixed(1)}ms</span>
          </div>
          <div className="performance-chart">
            <h4>FPS History</h4>
            <div className="chart-container">
              {performanceMonitorRef.current.history.fps.slice(-10).map((fps, index) => (
                <div 
                  key={index}
                  className="chart-bar"
                  style={{ height: `${(fps / 60) * 100}%` }}
                  title={`FPS: ${fps}`}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render with enhanced error handling
  if (state.hasError) {
    return (
      <div className="quantum-error-fallback">
        <div className="error-content">
          <h2>🌌 Quantum Visualization Recovery</h2>
          <p>{state.errorMessage}</p>
          <div className="quantum-recovery">
            <button 
              onClick={() => safeSetState({ hasError: false, errorMessage: '' })}
              className="quantum-recovery-button"
            >
              🔄 Restart Quantum Visualization
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="quantum-reset-button"
            >
              ⚡ Full Quantum Reset
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quantum-digital-thread-viz">
      {/* Quantum Loading Screen */}
     {state.isLoading && (
        <div className="quantum-loading-screen">
          <div className="quantum-loading-content">
            <div className="quantum-loader">
              <div className="quantum-orbital-ring"></div>
              <div className="quantum-particle"></div>
              <div className="quantum-wave"></div>
            </div>
            <h2>Initializing Quantum Digital Thread Platform</h2>
            <div className="quantum-progress-container">
              <div 
                className="quantum-progress-bar" 
                style={{ width: `${state.loadingProgress}%` }}
              ></div>
            </div>
            <p>Loading quantum visualization systems... {state.loadingProgress}%</p>
            <div className="loading-details">
              <p>• Initializing Quantum Engine</p>
              <p>• Configuring Visualization Systems</p>
              <p>• Loading {threadsData.length} Digital Threads</p>
              <p>• Preparing Analytics Dashboard</p>
            </div>
          </div>
        </div>
      )}

{/* 3D Quantum Visualization Container */}
<div 
  ref={containerRef} 
  className="quantum-viz-container"
  style={{ opacity: state.isLoading ? 0 : 1 }}
/>

{/* Enhanced Tooltip System */}
{state.tooltip.visible && (
  <div 
    className={`quantum-tooltip ${state.tooltip.type}`}
    style={{
      left: state.tooltip.x,
      top: state.tooltip.y
    }}
  >
    <div className="tooltip-content" dangerouslySetInnerHTML={{ __html: state.tooltip.content }} />
    {state.tooltip.elementData && (
      <div className="tooltip-analytics">
        {state.tooltip.elementData.healthScore && (
          <div className="tooltip-metric">
            <span>Health:</span>
            <span className="metric-value">{state.tooltip.elementData.healthScore}%</span>
          </div>
        )}
        {state.tooltip.elementData.connections && (
          <div className="tooltip-metric">
            <span>Connections:</span>
            <span className="metric-value">{state.tooltip.elementData.connections}</span>
          </div>
        )}
      </div>
    )}
  </div>
)}

    {/* Layout Selector */}
    {state.isInitialized && !state.isLoading && (
      <QuantumLayoutSelector
        currentLayout={currentLayout}
        isTransforming={isTransforming}
        transformToLayout={transformToLayout}
        resetToDefaultLayout={resetToDefaultLayout}
        availableLayouts={availableLayouts}
      />
    )}
    
    {/* Transformation Overlay */}
    {isTransforming && (
      <div className="layout-transition-overlay">
        <div className="transition-message">
          <div className="spinner" />
          <div>Transforming layout...</div>
        </div>
      </div>
    )}
    
    {/* Error Boundary */}
    {state.hasError && (
      <div className="quantum-error-overlay">
        <div className="error-content">
          <h2>🌌 Quantum System Anomaly Detected</h2>
          <p>{state.errorMessage}</p>
          <div className="error-actions">
            <button 
              onClick={() => window.location.reload()}
              className="quantum-btn primary"
            >
              🔄 Reinitialize Quantum System
            </button>
            <button 
              onClick={() => safeSetState({ hasError: false, errorMessage: '' })}
              className="quantum-btn secondary"
            >
              ⚡ Attempt Recovery
            </button>
          </div>
        </div>
      </div>
    )}


    {/* 🔍 ZOOM LEVEL DISPLAY - ADD HERE */}
    <ZoomLevelDisplay />

      {/* Enhanced Quantum Control Panel */}
      {state.showControls && <QuantumControlPanel />}

      {/* Quantum Analytics Panel */}
      {state.showAnalyticsPanel && <QuantumAnalyticsPanel />}

      {/* Performance Panel */}
      {state.showPerformancePanel && <PerformancePanel />}

      {/* Show Controls Button */}
      {!state.showControls && (
        <button 
          className="quantum-show-controls-btn"
          onClick={() => safeSetState(prev => ({ ...prev, showControls: true }))}
        >
          ⚙️ Quantum Controls
        </button>
      )}

      {/* Toggle Analytics Button */}
      <button 
        className={`quantum-analytics-btn ${state.showAnalyticsPanel ? 'active' : ''}`}
        onClick={() => safeSetState(prev => ({ ...prev, showAnalyticsPanel: !prev.showAnalyticsPanel }))}
      >
        📊 Analytics
      </button>

      {/* Toggle Performance Button */}
      <button 
        className={`quantum-performance-btn ${state.showPerformancePanel ? 'active' : ''}`}
        onClick={() => safeSetState(prev => ({ ...prev, showPerformancePanel: !prev.showPerformancePanel }))}
      >
        ⚡ Performance
      </button>

      {/* Quantum Performance Metrics */}
      <div className="quantum-performance-metrics">
        <div className="metric">
          <span className="value">{threadsData.length}</span>
          <span className="label">Threads</span>
        </div>
        <div className="metric">
          <span className="value">{state.goldenThreads.size}</span>
          <span className="label">Golden</span>
        </div>
        <div className="metric">
          <span className="value">{departmentsData.length}</span>
          <span className="label">Entities</span>
        </div>
        <div className="metric">
          <span className="value">{state.performance.fps}</span>
          <span className="label">FPS</span>
        </div>
      </div>

      {/* Quantum Full Screen Button */}
      <button 
        className={`quantum-fullscreen-btn ${state.isFullScreen ? 'active' : ''}`}
        onClick={toggleFullScreen}
        title={state.isFullScreen ? 'Exit Quantum Fullscreen' : 'Enter Quantum Fullscreen'}
      >
        {state.isFullScreen ? '📱' : '🖥️'}
      </button>

      {/* Quantum Instructions */}
      <div className="quantum-instructions">
        <p>🌌 <strong>Welcome to Quantum Digital Threads</strong></p>
        <p>🖱️ <strong>Explore:</strong> Click threads, hover for details, discover connections</p>
        <p>🌟 <strong>Golden Threads:</strong> Mark critical quantum workflows</p>
        <p>⚡ <strong>Quantum Metrics:</strong> Real-time optimization intelligence</p>
        <p>🔧 <strong>Analytics:</strong> Deep insights and recommendations</p>
      </div>

      {/* System Status */}
      <div className="system-status">
        <div className={`status-indicator ${state.isInitialized ? 'online' : 'offline'}`}>
          {state.isInitialized ? '🟢 SYSTEM ONLINE' : '🔴 SYSTEM OFFLINE'}
        </div>
        <div className="status-details">
          {state.animationPhase === 'quantum-active' && '🌠 Quantum Field Active'}
          {state.animationPhase === 'entrance' && '🚀 Initializing Quantum Space'}
        </div>
      </div>
    </div>
  );
};

// Enhanced Quantum CSS Styles
const quantumStyles = `
:root {
  --quantum-primary: #00f3ff;
  --quantum-secondary: #ff00ff;
  --quantum-accent: #00ff88;
  --quantum-warning: #ff4444;
  --quantum-success: #00ff00;
  --deep-space: #050518;
  --cosmic-purple: #1a0a2a;
  --nebula-blue: #0a0a3a;
  --stardust: rgba(255, 255, 255, 0.1);
  --event-horizon: rgba(0, 243, 255, 0.3);
  --quantum-glow: 0 0 20px rgba(0, 243, 255, 0.5);
  --golden-glow: 0 0 25px rgba(255, 215, 0, 0.6);
}

.quantum-digital-thread-viz {
  width: 100vw;
  height: 100vh;
  position: relative;
  background: radial-gradient(ellipse at center, var(--deep-space) 0%, var(--cosmic-purple) 100%);
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.quantum-viz-container {
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.quantum-viz-container:active {
  cursor: grabbing;
}

.quantum-viz-canvas {
  display: block;
  outline: none;
}

/* Quantum Loading Screen */
.quantum-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, var(--deep-space) 0%, #000 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.quantum-loading-content {
  text-align: center;
  background: rgba(10, 10, 42, 0.95);
  padding: 3rem;
  border-radius: 20px;
  border: 1px solid var(--event-horizon);
  backdrop-filter: blur(20px);
  box-shadow: var(--quantum-glow);
  max-width: 600px;
}

.quantum-loader {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 2rem;
}

.quantum-orbital-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid var(--quantum-primary);
  border-radius: 50%;
  animation: quantum-spin 2s linear infinite;
}

.quantum-particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background: var(--quantum-secondary);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: quantum-pulse 1.5s ease-in-out infinite alternate;
  box-shadow: 0 0 20px var(--quantum-secondary);
}

.quantum-wave {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border: 2px solid var(--quantum-accent);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: quantum-expand 2s ease-out infinite;
}

.quantum-progress-container {
  width: 400px;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin: 2rem auto;
  overflow: hidden;
  position: relative;
}

.quantum-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--quantum-primary), var(--quantum-accent));
  border-radius: 4px;
  transition: width 0.3s ease;
  position: relative;
}

.quantum-progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  animation: quantum-shimmer 2s infinite;
}

.loading-details {
  margin-top: 2rem;
  text-align: left;
  color: rgba(255, 255, 255, 0.8);
}

.loading-details p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
}

/* Quantum Control Panel */
.quantum-control-panel {
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(10, 10, 42, 0.95);
  border: 1px solid var(--event-horizon);
  border-radius: 15px;
  padding: 20px;
  width: 380px;
  max-height: 80vh;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 30px rgba(0, 243, 255, 0.2);
  z-index: 100;
}

.quantum-analytics-panel {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(10, 10, 42, 0.95);
  border: 1px solid var(--event-horizon);
  border-radius: 15px;
  padding: 20px;
  width: 350px;
  max-height: 80vh;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 30px rgba(0, 243, 255, 0.2);
  z-index: 100;
}

.performance-panel {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(10, 10, 42, 0.95);
  border: 1px solid var(--event-horizon);
  border-radius: 15px;
  padding: 20px;
  width: 300px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 30px rgba(0, 243, 255, 0.2);
  z-index: 100;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.panel-header h3 {
  color: var(--quantum-primary);
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.hide-panel-btn {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 6px;
  color: #f44336;
  cursor: pointer;
  padding: 8px 12px;
  transition: all 0.3s ease;
  font-size: 12px;
}

.hide-panel-btn:hover {
  background: rgba(244, 67, 54, 0.2);
  border-color: #f44336;
  transform: scale(1.05);
}

.control-section {
  margin-bottom: 25px;
}

.control-section h4 {
  color: var(--quantum-primary);
  margin-bottom: 15px;
  font-size: 1rem;
  border-left: 3px solid var(--quantum-primary);
  padding-left: 10px;
}

.control-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.control-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--event-horizon);
  border-radius: 8px;
  color: var(--quantum-primary);
  cursor: pointer;
  padding: 10px 15px;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;
  font-size: 12px;
}

.control-btn:hover {
  background: rgba(0, 243, 255, 0.1);
  transform: translateY(-2px);
  box-shadow: var(--quantum-glow);
}

.control-btn.active {
  background: rgba(0, 243, 255, 0.2);
  border-color: var(--quantum-primary);
  box-shadow: 0 0 15px rgba(0, 243, 255, 0.3);
}

.slider-group {
  margin-bottom: 15px;
}

.slider-group label {
  display: block;
  color: var(--quantum-primary);
  margin-bottom: 8px;
  font-size: 12px;
}

.slider-group input {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  outline: none;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.slider-group input:hover {
  opacity: 1;
}

.slider-group input::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--quantum-primary);
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 243, 255, 0.5);
}

/* Quantum Metrics */
.quantum-metrics {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.metric-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.metric-bar span:first-child {
  width: 100px;
  color: var(--quantum-primary);
}

.bar-container {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--quantum-primary), var(--quantum-accent));
  border-radius: 4px;
  transition: width 0.5s ease;
}

.metric-bar span:last-child {
  width: 40px;
  text-align: right;
  color: var(--quantum-accent);
  font-weight: bold;
}

/* Thread Items */
.threads-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.thread-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.02);
}

.thread-item:hover {
  background: rgba(255, 255, 255, 0.05);
  transform: translateX(5px);
}

.thread-item.golden {
  border-left: 4px solid #ffd700;
  background: rgba(255, 215, 0, 0.1);
  box-shadow: var(--golden-glow);
}

.thread-item.selected {
  border-color: var(--quantum-accent);
  background: rgba(0, 255, 136, 0.1);
}

.thread-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  cursor: pointer;
}

.thread-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 5px currentColor;
}

.thread-name {
  flex: 1;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.golden-badge, .critical-badge, .value-badge {
  font-size: 10px;
}

.critical-badge {
  color: var(--quantum-warning);
}

.value-badge {
  color: var(--quantum-accent);
}

.thread-actions {
  display: flex;
  gap: 5px;
}

.thread-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 10px;
}

.thread-select:hover {
  background: rgba(0, 243, 255, 0.2);
}

.thread-select.selected {
  background: var(--quantum-accent);
  color: #000;
  border-color: var(--quantum-accent);
}

.golden-thread-btn {
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 6px;
  color: #ffd700;
  cursor: pointer;
  padding: 4px 6px;
  transition: all 0.3s ease;
  font-size: 10px;
}

.golden-thread-btn:hover,
.golden-thread-btn.active {
  background: rgba(255, 215, 0, 0.3);
  border-color: #ffd700;
  transform: scale(1.1);
}

.golden-thread-btn.active {
  background: #ffd700;
  color: #000;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
}

/* Analytics */
.analytics-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.analytics-section h4 {
  color: var(--quantum-primary);
  margin-bottom: 15px;
  font-size: 1rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.stat {
  text-align: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s ease;
}

.stat:hover {
  transform: translateY(-2px);
}

.stat .value {
  display: block;
  font-size: 1.8rem;
  font-weight: bold;
  background: linear-gradient(135deg, var(--quantum-primary), var(--quantum-accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 5px;
}

.stat .label {
  font-size: 0.8rem;
  color: #a0a0ff;
}

.health-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.health-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
}

.health-item .thread-name {
  width: 140px;
  color: #ffffff;
  font-size: 11px;
}

.health-bar {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.health-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

.health-fill.excellent {
  background: linear-gradient(90deg, #00ff00, #00ff88);
}

.health-fill.good {
  background: linear-gradient(90deg, #ffff00, #ffaa00);
}

.health-fill.poor {
  background: linear-gradient(90deg, #ff4444, #ff0000);
}

.health-score {
  width: 30px;
  text-align: right;
  font-weight: bold;
  color: var(--quantum-accent);
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.recommendation {
  padding: 12px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  border-left: 4px solid var(--quantum-primary);
}

.recommendation.high {
  border-left-color: var(--quantum-warning);
}

.recommendation.medium {
  border-left-color: var(--quantum-accent);
}

.rec-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.rec-title {
  font-weight: bold;
  color: #ffffff;
  font-size: 12px;
}

.rec-priority {
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
}

.rec-priority.high {
  background: rgba(255, 68, 68, 0.2);
  color: #ff4444;
}

.rec-priority.medium {
  background: rgba(0, 255, 136, 0.2);
  color: #00ff88;
}

.rec-message {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
  line-height: 1.4;
}

.rec-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.rec-action {
  padding: 2px 6px;
  background: rgba(0, 243, 255, 0.1);
  border-radius: 3px;
  font-size: 9px;
  color: var(--quantum-primary);
}

.selection-details {
  font-size: 12px;
  color: #ffffff;
}

.selection-details p {
  margin: 5px 0;
  display: flex;
  justify-content: space-between;
}

.selection-details strong {
  color: var(--quantum-primary);
}

/* Performance */
.performance-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.performance-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.metric-label {
  color: var(--quantum-primary);
  font-size: 12px;
}

.metric-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--quantum-accent);
}

.metric-trend {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.6);
}

.chart-container {
  display: flex;
  align-items: end;
  gap: 2px;
  height: 60px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.chart-bar {
  flex: 1;
  background: linear-gradient(to top, var(--quantum-primary), var(--quantum-accent));
  border-radius: 2px 2px 0 0;
  min-height: 2px;
  transition: height 0.3s ease;
}

/* Quantum Tooltip */
.quantum-tooltip {
  position: fixed;
  background: rgba(10, 10, 42, 0.98);
  border: 1px solid var(--quantum-primary);
  border-radius: 10px;
  padding: 15px;
  max-width: 400px;
  z-index: 1000;
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), var(--quantum-glow);
  pointer-events: none;
  font-size: 12px;
  line-height: 1.5;
}

.quantum-tooltip.department {
  border-color: var(--quantum-primary);
}

.quantum-tooltip.thread {
  border-color: var(--quantum-accent);
}

.tooltip-content {
  color: #ffffff;
  margin-bottom: 10px;
}

.tooltip-content strong {
  color: var(--quantum-primary);
}

.tooltip-content em {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
}

.tooltip-metrics {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 10px;
}

.tooltip-metrics .metric {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 11px;
}

.tooltip-metrics .label {
  color: #a0a0ff;
}

.tooltip-metrics .value {
  color: var(--quantum-accent);
  font-weight: bold;
}

/* Quantum Performance Metrics */
.quantum-performance-metrics {
  position: absolute;
  top: 20px;
  right: 390px;
  display: flex;
  gap: 15px;
  background: rgba(10, 10, 42, 0.8);
  border: 1px solid var(--event-horizon);
  border-radius: 10px;
  padding: 15px;
  backdrop-filter: blur(10px);
  z-index: 50;
}

.quantum-performance-metrics .metric {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.quantum-performance-metrics .value {
  font-size: 1.2rem;
  font-weight: bold;
  background: linear-gradient(135deg, var(--quantum-primary), var(--quantum-accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.quantum-performance-metrics .label {
  font-size: 0.7rem;
  color: #a0a0ff;
}

/* Quantum Buttons */
.quantum-show-controls-btn,
.quantum-analytics-btn,
.quantum-performance-btn,
.quantum-fullscreen-btn {
  position: absolute;
  background: rgba(10, 10, 42, 0.8);
  border: 1px solid var(--event-horizon);
  border-radius: 8px;
  color: var(--quantum-primary);
  cursor: pointer;
  padding: 10px 15px;
  transition: all 0.3s ease;
  z-index: 50;
  backdrop-filter: blur(10px);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.quantum-show-controls-btn {
  top: 20px;
  left: 20px;
}

.quantum-analytics-btn {
  top: 20px;
  right: 390px;
  margin-right: 10px;
}

.quantum-performance-btn {
  top: 70px;
  right: 390px;
  margin-right: 10px;
}

.quantum-fullscreen-btn {
  bottom: 20px;
  right: 20px;
}

.quantum-show-controls-btn:hover,
.quantum-analytics-btn:hover,
.quantum-performance-btn:hover,
.quantum-fullscreen-btn:hover {
  background: rgba(0, 243, 255, 0.1);
  transform: translateY(-2px);
  box-shadow: var(--quantum-glow);
}

.quantum-analytics-btn.active,
.quantum-performance-btn.active,
.quantum-fullscreen-btn.active {
  background: rgba(0, 243, 255, 0.2);
  border-color: var(--quantum-primary);
}

/* Quantum Instructions */
.quantum-instructions {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(10, 10, 42, 0.8);
  border: 1px solid var(--event-horizon);
  border-radius: 10px;
  padding: 15px;
  max-width: 320px;
  backdrop-filter: blur(10px);
  z-index: 50;
  font-size: 11px;
  line-height: 1.4;
}

.quantum-instructions p {
  margin: 5px 0;
  color: #ffffff;
}

.quantum-instructions strong {
  color: var(--quantum-primary);
}

/* System Status */
.system-status {
  position: absolute;
  bottom: 20px;
  right: 390px;
  background: rgba(10, 10, 42, 0.8);
  border: 1px solid var(--event-horizon);
  border-radius: 10px;
  padding: 10px 15px;
  backdrop-filter: blur(10px);
  z-index: 50;
  font-size: 11px;
}

.status-indicator {
  font-weight: bold;
  margin-bottom: 5px;
}

.status-indicator.online {
  color: var(--quantum-success);
}

.status-indicator.offline {
  color: var(--quantum-warning);
}

.status-details {
  color: rgba(255, 255, 255, 0.8);
  font-size: 10px;
}

/* Quantum Error Fallback */
.quantum-error-fallback {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a0a2a 0%, #050518 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.quantum-error-fallback .error-content {
  background: rgba(10, 10, 42, 0.95);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  max-width: 500px;
  backdrop-filter: blur(20px);
}

.quantum-error-fallback h2 {
  color: #f44336;
  margin-bottom: 15px;
}

.quantum-error-fallback p {
  color: #ffffff;
  margin-bottom: 20px;
  opacity: 0.9;
}

.quantum-recovery {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}

.quantum-recovery-button,
.quantum-reset-button {
  background: linear-gradient(135deg, var(--quantum-primary) 0%, #0099cc 100%);
  border: none;
  border-radius: 8px;
  color: #050518;
  cursor: pointer;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 12px;
}

.quantum-reset-button {
  background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
}

.quantum-recovery-button:hover,
.quantum-reset-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 243, 255, 0.4);
}

.quantum-reset-button:hover {
  box-shadow: 0 8px 25px rgba(255, 68, 68, 0.4);
}

/* Quantum Animations */
@keyframes quantum-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes quantum-pulse {
  0% { 
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 0 20px var(--quantum-secondary);
  }
  100% { 
    transform: translate(-50%, -50%) scale(1.3);
    box-shadow: 0 0 40px var(--quantum-secondary), 0 0 60px var(--quantum-primary);
  }
}

@keyframes quantum-expand {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

@keyframes quantum-shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .quantum-control-panel,
  .quantum-analytics-panel {
    width: 300px;
    left: 10px;
    top: 10px;
  }
  
  .quantum-analytics-panel {
    left: auto;
    right: 10px;
  }
  
  .performance-panel {
    width: 280px;
    left: 10px;
    bottom: 10px;
  }
  
  .quantum-performance-metrics {
    top: 10px;
    right: 320px;
    flex-direction: column;
    gap: 10px;
  }
  
  .quantum-analytics-btn,
  .quantum-performance-btn {
    right: 320px;
  }
  
  .system-status {
    right: 320px;
    bottom: 10px;
  }
  
  .control-group {
    flex-direction: column;
  }
  
  .control-btn {
    min-width: auto;
  }
  
  .quantum-instructions {
    max-width: 280px;
    font-size: 10px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* Scrollbar Styling */
.quantum-control-panel::-webkit-scrollbar,
.quantum-analytics-panel::-webkit-scrollbar,
.threads-list::-webkit-scrollbar {
  width: 6px;
}

.quantum-control-panel::-webkit-scrollbar-track,
.quantum-analytics-panel::-webkit-scrollbar-track,
.threads-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.quantum-control-panel::-webkit-scrollbar-thumb,
.quantum-analytics-panel::-webkit-scrollbar-thumb,
.threads-list::-webkit-scrollbar-thumb {
  background: var(--quantum-primary);
  border-radius: 3px;
}

.quantum-control-panel::-webkit-scrollbar-thumb:hover,
.quantum-analytics-panel::-webkit-scrollbar-thumb:hover,
.threads-list::-webkit-scrollbar-thumb:hover {
  background: var(--quantum-accent);
}

/* Thread Management Container */
.thread-management-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: rgba(5, 5, 24, 0.8);
  border-radius: 12px;
  padding: 15px;
  border: 1px solid rgba(0, 243, 255, 0.2);
}

/* Visibility Controls Panel */
.visibility-controls-panel {
  background: rgba(10, 10, 42, 0.6);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(0, 243, 255, 0.15);
}

.visibility-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.visibility-header h5 {
  color: var(--quantum-primary);
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.visibility-stats {
  display: flex;
  gap: 15px;
}

.visibility-stats .stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  min-width: 70px;
}

.visibility-stats .stat-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.visibility-stats .stat-value {
  font-size: 18px;
  font-weight: bold;
}

.visible-count { color: #00ff88; }
.hidden-count { color: #ff4444; }
.selected-count { color: #00f3ff; }

.visibility-quick-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}

.visibility-action-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.visibility-action-btn {
  padding: 10px 8px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 36px;
  font-weight: 500;
}

.visibility-action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.visibility-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.show-all-btn {
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(0, 255, 136, 0.1));
  border: 1px solid rgba(0, 255, 136, 0.3);
  color: #00ff88;
}

.hide-all-btn {
  background: linear-gradient(135deg, rgba(255, 68, 68, 0.2), rgba(255, 68, 68, 0.1));
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #ff4444;
}

.invert-btn {
  background: linear-gradient(135deg, rgba(147, 112, 219, 0.2), rgba(147, 112, 219, 0.1));
  border: 1px solid rgba(147, 112, 219, 0.3);
  color: #9370db;
}

.show-golden-btn {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.1));
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: #ffd700;
}

.show-critical-btn {
  background: linear-gradient(135deg, rgba(255, 68, 68, 0.2), rgba(255, 68, 68, 0.1));
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #ff4444;
}

.show-value-btn {
  background: linear-gradient(135deg, rgba(0, 243, 255, 0.2), rgba(0, 243, 255, 0.1));
  border: 1px solid rgba(0, 243, 255, 0.3);
  color: #00f3ff;
}

.show-selected-btn {
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(0, 255, 136, 0.1));
  border: 1px solid rgba(0, 255, 136, 0.3);
  color: #00ff88;
}

.hide-selected-btn {
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.2), rgba(255, 107, 107, 0.1));
  border: 1px solid rgba(255, 107, 107, 0.3);
  color: #ff6b6b;
}

.show-healthy-btn {
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(0, 255, 136, 0.1));
  border: 1px solid rgba(0, 255, 136, 0.3);
  color: #00ff88;
}

.visibility-filter {
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.filter-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.filter-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.filter-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
}

.filter-description {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

/* Selection Controls Panel */
.selection-controls-panel {
  background: rgba(10, 10, 42, 0.6);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(0, 255, 136, 0.15);
}

.selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.selection-header h5 {
  color: var(--quantum-accent);
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.selected-count-display {
  background: rgba(0, 255, 136, 0.2);
  color: var(--quantum-accent);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.selection-quick-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.selection-action-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.selection-action-btn {
  padding: 10px 8px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 36px;
  font-weight: 500;
}

.selection-action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.selection-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.select-all-btn {
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(0, 255, 136, 0.1));
  border: 1px solid rgba(0, 255, 136, 0.3);
  color: #00ff88;
}

.deselect-all-btn {
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.2), rgba(255, 107, 107, 0.1));
  border: 1px solid rgba(255, 107, 107, 0.3);
  color: #ff6b6b;
}

.select-visible-btn {
  background: linear-gradient(135deg, rgba(0, 243, 255, 0.2), rgba(0, 243, 255, 0.1));
  border: 1px solid rgba(0, 243, 255, 0.3);
  color: #00f3ff;
}

.optimize-btn {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.1));
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: #ffd700;
}

.clear-btn {
  background: linear-gradient(135deg, rgba(147, 112, 219, 0.2), rgba(147, 112, 219, 0.1));
  border: 1px solid rgba(147, 112, 219, 0.3);
  color: #9370db;
}

.zoom-to-selected-btn {
  background: linear-gradient(135deg, rgba(0, 243, 255, 0.2), rgba(0, 243, 255, 0.1));
  border: 1px solid rgba(0, 243, 255, 0.3);
  color: #00f3ff;
}

/* Thread List Container */
.thread-list-container {
  background: rgba(10, 10, 42, 0.6);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.thread-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.list-header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.thread-list-title {
  color: var(--quantum-primary);
  font-weight: 600;
  font-size: 13px;
}

.thread-list-stats {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  gap: 10px;
}

.list-action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 6px 10px;
  font-size: 10px;
  transition: all 0.3s ease;
}

.list-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.thread-list-search {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.thread-search-input {
  flex: 1;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  font-size: 12px;
}

.thread-search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.thread-sort-select {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  font-size: 12px;
  cursor: pointer;
}

.threads-list-scroll {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

/* Custom scrollbar */
.threads-list-scroll::-webkit-scrollbar {
  width: 6px;
}

.threads-list-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.threads-list-scroll::-webkit-scrollbar-thumb {
  background: var(--quantum-primary);
  border-radius: 3px;
}

/* Enhanced Thread Items */
.thread-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  margin-bottom: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.thread-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(0, 243, 255, 0.3);
  transform: translateX(5px);
}

.thread-item.selected {
  background: rgba(0, 255, 136, 0.1);
  border-color: var(--quantum-accent);
  box-shadow: 0 0 15px rgba(0, 255, 136, 0.1);
}

.thread-item.golden {
  background: rgba(255, 215, 0, 0.08);
  border-left: 3px solid #ffd700;
}

.thread-item.critical {
  background: rgba(255, 68, 68, 0.08);
  border-left: 3px solid #ff4444;
}

.thread-item.value-stream {
  background: rgba(0, 243, 255, 0.08);
  border-left: 3px solid #00f3ff;
}

.thread-item.hidden {
  opacity: 0.5;
  background: rgba(255, 255, 255, 0.02);
}

.thread-visibility-toggle {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s ease;
  font-size: 14px;
  z-index: 2;
}

.thread-visibility-toggle:hover {
  background: rgba(0, 243, 255, 0.2);
  transform: scale(1.1);
}

.thread-color-indicator {
  display: flex;
  align-items: center;
}

.thread-color-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: 0 0 5px currentColor;
}

.thread-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.thread-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.thread-name {
  font-weight: 600;
  font-size: 12px;
  color: #ffffff;
}

.thread-item.hidden .thread-name {
  color: rgba(255, 255, 255, 0.6);
}

.thread-badges {
  display: flex;
  gap: 4px;
}

.thread-badges span {
  font-size: 10px;
  cursor: help;
}

.golden-badge { color: #ffd700; }
.critical-badge { color: #ff4444; }
.value-badge { color: #00f3ff; }
.hidden-badge { color: rgba(255, 255, 255, 0.5); }
.healthy-badge { color: #00ff88; }
.unhealthy-badge { color: #ff4444; }

.thread-description {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.3;
}

.thread-checkbox {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.checkbox-indicator {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.3s ease;
}

.checkbox-indicator.checked {
  background: var(--quantum-accent);
  border-color: var(--quantum-accent);
  color: #000;
}

.thread-list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-stats {
  display: flex;
  gap: 15px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}

.footer-action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 6px 10px;
  font-size: 10px;
  transition: all 0.3s ease;
}

.footer-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .visibility-action-row,
  .selection-action-row {
    grid-template-columns: 1fr;
  }
  
  .visibility-stats {
    flex-direction: column;
    gap: 8px;
  }
  
  .thread-list-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}


/* LAYOUT MANAGEMENT STYLES */
.layout-management-container {
  margin-bottom: 20px;
}

.layout-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 15px;
}

.layout-stat-item {
  text-align: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.stat-label {
  display: block;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
}

.stat-value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--quantum-primary);
}

.layout-actions {
  display: flex;
  gap: 10px;
}

.layout-action-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.optimize-layout-btn {
  background: linear-gradient(145deg, rgba(0, 243, 255, 0.9), rgba(0, 200, 255, 0.9));
  color: #000;
}

.optimize-layout-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, rgba(0, 255, 255, 0.9), rgba(0, 220, 255, 0.9));
  transform: translateY(-2px);
}

.reset-layout-btn {
  background: rgba(255, 255, 255, 0.1);
  color: var(--quantum-primary);
  border: 1px solid var(--event-horizon);
}

/* ZOOM CONTROLS STYLES */
.zoom-controls-container {
  margin-bottom: 20px;
}

.zoom-presets {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
}

.zoom-preset-btn {
  padding: 10px;
  border: 1px solid var(--event-horizon);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: var(--quantum-primary);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;
}

.zoom-preset-btn:hover {
  background: rgba(0, 243, 255, 0.1);
  transform: translateY(-2px);
}

.zoom-slider-container {
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.zoom-slider-container label {
  display: block;
  color: var(--quantum-primary);
  margin-bottom: 10px;
  font-size: 12px;
}

.zoom-slider {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  outline: none;
}

.zoom-ticks {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}

/* VISUALIZATION CONTROLS */
.visualization-controls {
  margin-bottom: 20px;
}

.toggle-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.toggle-item input[type="checkbox"] {
  accent-color: var(--quantum-primary);
}

.toggle-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
}

.speed-controls {
  margin-bottom: 15px;
}

.play-pause-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(145deg, rgba(0, 243, 255, 0.9), rgba(0, 200, 255, 0.9));
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.play-pause-btn:hover {
  background: linear-gradient(145deg, rgba(0, 255, 255, 0.9), rgba(0, 220, 255, 0.9));
  transform: translateY(-2px);
}

.play-pause-btn.paused {
  background: linear-gradient(145deg, rgba(255, 100, 100, 0.9), rgba(200, 50, 50, 0.9));
}

/* SECTION HEADER */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.section-header:hover {
  background: rgba(255, 255, 255, 0.05);
  padding-left: 10px;
  padding-right: 10px;
  border-radius: 6px;
}

.section-header h4 {
  margin: 0;
  color: var(--quantum-primary);
  font-size: 14px;
}

.expand-icon {
  color: var(--quantum-primary);
  font-size: 12px;
  transition: transform 0.3s ease;
}

.section-header:hover .expand-icon {
  transform: scale(1.2);
}

/* SCROLLBAR STYLING */
.threads-list-scroll::-webkit-scrollbar {
  width: 6px;
}

.threads-list-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.threads-list-scroll::-webkit-scrollbar-thumb {
  background: var(--quantum-primary);
  border-radius: 3px;
}

.threads-list-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--quantum-accent);
}

/* Add to quantumStyles (around the end of the file) */
.zoom-level-display {
  position: fixed;
  bottom: 80px;
  right: 20px;
  background: rgba(10, 10, 42, 0.9);
  border: 1px solid var(--quantum-accent);
  border-radius: 10px;
  padding: 12px 20px;
  color: var(--quantum-primary);
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  z-index: 100;
  box-shadow: var(--quantum-glow);
  display: flex;
  align-items: center;
  gap: 15px;
}

.zoom-level-display.ultra-zoom {
  border-color: #00ff00;
  box-shadow: 0 0 20px rgba(0, 255, 0, 0.5);
}

.zoom-level-display.high-zoom {
  border-color: #ffff00;
  box-shadow: 0 0 20px rgba(255, 255, 0, 0.5);
}

.zoom-readability {
  margin-left: 10px;
  font-size: 12px;
  color: var(--quantum-accent);
  font-weight: normal;
}

.zoom-performance {
  margin-left: 15px;
  font-size: 11px;
  color: #a0a0ff;
  padding: 3px 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

// Add these styles to the existing quantumStyles:

/* Thread Control Panel */
.thread-control-btn {
  position: absolute;
  bottom: 80px;
  right: 20px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  color: #ffd700;
  cursor: pointer;
  padding: 10px 15px;
  transition: all 0.3s ease;
  font-size: 12px;
  z-index: 50;
}

.thread-control-btn:hover {
  background: rgba(255, 215, 0, 0.2);
  transform: translateY(-2px);
  box-shadow: var(--golden-glow);
}

.thread-control-btn.active {
  background: rgba(255, 215, 0, 0.3);
  border-color: #ffd700;
  box-shadow: 0 0 15px rgba(255, 215, 0, 0.4);
}

/* Thread Control Panel Container */
.thread-control-panel-container {
  position: absolute;
  top: 20px;
  right: 400px;
  background: rgba(10, 10, 42, 0.95);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 15px;
  padding: 20px;
  width: 320px;
  max-height: 70vh;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  box-shadow: var(--golden-glow);
  z-index: 100;
}

/* Make sure this is the last line of your CSS styles */
.system-status .status-details {
  font-size: 12px;
  color: var(--quantum-accent);
}
`; // ← ADD THIS CLOSING BACKTICK HERE


// Inject quantum styles
const quantumStyleSheet = document.createElement('style');
quantumStyleSheet.textContent = quantumStyles;
document.head.appendChild(quantumStyleSheet);

// Export with quantum error boundary
export default function UltimateDigitalThreadVisualizationWithErrorBoundary() {
  return (
    <VisualizationErrorBoundary>
      <UltimateDigitalThreadVisualization />
    </VisualizationErrorBoundary>
  );
}